import { useState, useEffect, useCallback, useRef } from "react";
import { vehiclesApi, affiliateApi } from "../services/api";
import ModuleHeader from "../components/shared/ModuleHeader";
import SavingNotice from "../components/shared/SavingNotice";
import CenteredModal from "../components/shared/CenteredModal";
import ConfirmModal from "../components/shared/ConfirmModal";
import { SheetLogoRow } from "../components/shared/SheetChrome";
import useMinLoad from "../hooks/useMinLoad";
import BackButtonTrap from "../components/shared/BackButtonTrap";
import useFormPresentation from "../hooks/useFormPresentation";
import { useAuth } from "../hooks/useAuth";

const IMG_VEHICLES = "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1400&q=75&auto=format&fit=crop";
// ── Constants ─────────────────────────────────────────────────────────────────

const VEHICLE_TYPES = [
  { value: "car",        label: "Car",        icon: "🚗" },
  { value: "truck",      label: "Truck",       icon: "🛻" },
  { value: "suv",        label: "SUV",         icon: "🚙" },
  { value: "van",        label: "Van",         icon: "🚐" },
  { value: "motorcycle", label: "Motorcycle",  icon: "🏍️" },
  { value: "boat",       label: "Boat",        icon: "⛵" },
  { value: "rv",         label: "RV",          icon: "🏕️" },
  { value: "other",      label: "Other",       icon: "🚙" },
];

const SERVICE_TYPE_LABELS = {
  oil_change:      "Oil Change",
  tire_rotation:   "Tire Rotation",
  tire_replacement:"Tire Replacement",
  brake_pad:       "Brake Pads",
  brake_rotor:     "Brake Rotors",
  transmission:    "Transmission Service",
  battery:         "Battery",
  coolant:         "Coolant Flush",
  air_filter:      "Air Filter",
  cabin_filter:    "Cabin Filter",
  spark_plug:      "Spark Plugs",
  wiper_blade:     "Wiper Blades",
  inspection:      "Inspection",
  recall_repair:   "Recall Repair",
  alignment:       "Wheel Alignment",
  ac_service:      "A/C Service",
  other:           "Other",
};

const SERVICE_COMBOS = [
  { key: "oil_tire", label: "Oil change + Tire rotation", types: ["oil_change", "tire_rotation"] },
  { key: "full_service", label: "Full service", types: ["oil_change", "tire_rotation", "air_filter", "cabin_filter", "inspection"] },
];

const OIL_INTERVAL_PRESETS = [
  { key: "conventional", label: "Conventional", miles: 3000, months: 3 },
  { key: "synthetic_blend", label: "Synthetic blend", miles: 5000, months: 6 },
  { key: "full_synthetic", label: "Full synthetic", miles: 7500, months: 12 },
  { key: "extended_synthetic", label: "Extended synthetic", miles: 10000, months: 12 },
  { key: "manufacturer", label: "Use vehicle schedule", miles: null, months: null },
  { key: "custom", label: "Custom...", miles: null, months: null },
];

const SERVICE_LOG_FILTERS = [
  { key: "all", label: "All" },
  { key: "oil", label: "Oil" },
  { key: "tires", label: "Tires" },
  { key: "repairs", label: "Repairs" },
];

const REPAIR_SERVICE_TYPES = new Set([
  "brake", "brake_pad", "brake_rotor", "transmission", "battery", "coolant",
  "spark_plug", "recall_repair", "alignment", "ac_service", "other",
]);

const MANUAL_SERVICE_ENTRY = "__manual_service_entry__";

// ── Vehicle catalog (make → models) ──────────────────────────────────────────

const VEHICLE_MAKES = {
  "Acura":         ["ILX","MDX","RDX","RLX","TLX"],
  "Audi":          ["A3","A4","A5","A6","A7","A8","Q3","Q5","Q7","Q8","e-tron","TT"],
  "BMW":           ["2 Series","3 Series","4 Series","5 Series","7 Series","8 Series","X1","X3","X5","X6","X7","i3","i4","iX"],
  "Buick":         ["Enclave","Encore","Encore GX","Envision","LaCrosse"],
  "Cadillac":      ["CT4","CT5","Escalade","Escalade ESV","Lyriq","XT4","XT5","XT6"],
  "Chevrolet":     ["Blazer","Camaro","Colorado","Corvette","Equinox","Malibu","Silverado 1500","Silverado 2500HD","Silverado 3500HD","Suburban","Tahoe","Traverse","Trailblazer","Trax"],
  "Chrysler":      ["300","Pacifica","Voyager"],
  "Dodge":         ["Challenger","Charger","Durango","Hornet"],
  "Ford":          ["Bronco","Bronco Sport","Edge","Escape","Expedition","Explorer","F-150","F-250 Super Duty","F-350 Super Duty","Maverick","Mustang","Mustang Mach-E","Ranger","Transit"],
  "Genesis":       ["G70","G80","G90","GV70","GV80"],
  "GMC":           ["Acadia","Canyon","Envoy","Sierra 1500","Sierra 2500HD","Sierra 3500HD","Terrain","Yukon","Yukon XL"],
  "Honda":         ["Accord","Civic","CR-V","HR-V","Odyssey","Passport","Pilot","Ridgeline"],
  "Hyundai":       ["Elantra","IONIQ 5","IONIQ 6","Kona","Palisade","Santa Cruz","Santa Fe","Sonata","Tucson","Venue"],
  "Infiniti":      ["Q50","Q60","QX50","QX55","QX60","QX80"],
  "Jeep":          ["Cherokee","Compass","Gladiator","Grand Cherokee","Grand Cherokee L","Renegade","Wrangler","Wrangler Unlimited"],
  "Kia":           ["Carnival","EV6","EV9","K5","Niro","Seltos","Sorento","Soul","Sportage","Stinger","Telluride"],
  "Land Rover":    ["Defender","Discovery","Discovery Sport","Range Rover","Range Rover Evoque","Range Rover Sport","Range Rover Velar"],
  "Lexus":         ["ES","GX","IS","LC","LS","LX","NX","RX","RX L","UX"],
  "Lincoln":       ["Aviator","Corsair","Navigator","Navigator L","Nautilus"],
  "Mazda":         ["CX-30","CX-5","CX-50","CX-70","CX-90","Mazda3","Mazda3 Sedan","MX-5 Miata","MX-30"],
  "Mercedes-Benz": ["A-Class","C-Class","CLA","E-Class","EQB","EQE","EQS","GLA","GLB","GLC","GLE","GLS","S-Class","Sprinter"],
  "Mitsubishi":    ["Eclipse Cross","Outlander","Outlander Sport"],
  "Nissan":        ["Altima","Armada","Frontier","Kicks","Leaf","Maxima","Murano","Pathfinder","Rogue","Rogue Sport","Sentra","Titan","Versa","Z"],
  "Ram":           ["1500","2500","3500","ProMaster","ProMaster City"],
  "Subaru":        ["Ascent","BRZ","Crosstrek","Forester","Impreza","Legacy","Outback","Solterra","WRX"],
  "Tesla":         ["Cybertruck","Model 3","Model S","Model X","Model Y","Roadster"],
  "Toyota":        ["4Runner","bZ4X","Camry","Corolla","Corolla Cross","GR86","Highlander","Land Cruiser","Prius","Prius Prime","RAV4","RAV4 Prime","Sequoia","Sienna","Tacoma","Tundra","Venza"],
  "Volkswagen":    ["Atlas","Atlas Cross Sport","Golf","ID.4","ID.Buzz","Jetta","Taos","Tiguan"],
  "Volvo":         ["C40 Recharge","EX30","EX40","EX90","S60","S90","V60","XC40","XC60","XC90"],
};

const MAKE_LIST = Object.keys(VEHICLE_MAKES).sort();

const YEAR_LIST = Array.from({ length: 2026 - 1980 + 1 }, (_, i) => 2026 - i);

// Trims keyed by "Make|Model"
const VEHICLE_TRIMS = {
  "Toyota|Camry":        ["LE","SE","SE Nightshade","XLE","XSE","TRD","Hybrid LE","Hybrid SE","Hybrid XLE","Hybrid XSE"],
  "Toyota|Corolla":      ["L","LE","SE","XLE","XSE","GR","Hybrid LE","Hybrid SE","Hybrid XLE"],
  "Toyota|RAV4":         ["LE","XLE","XLE Premium","TRD Off-Road","Adventure","Limited","Hybrid LE","Hybrid XLE","Hybrid XLE Premium","Hybrid Limited","Prime SE","Prime XSE"],
  "Toyota|Highlander":   ["L","LE","XLE","XSE","Limited","Platinum","Hybrid LE","Hybrid XLE","Hybrid Limited","Hybrid Platinum"],
  "Toyota|Tacoma":       ["SR","SR5","TRD Sport","TRD Off-Road","Limited","TRD Pro","Trail Edition"],
  "Toyota|Tundra":       ["SR","SR5","Limited","Platinum","1794 Edition","TRD Pro","Capstone"],
  "Toyota|Sienna":       ["LE","XLE","XSE","Limited","Platinum"],
  "Toyota|Prius":        ["L Eco","LE","XLE","Limited","Prime SE","Prime XSE","Prime XSE Premium"],
  "Toyota|4Runner":      ["SR5","SR5 Premium","TRD Sport","TRD Off-Road","TRD Off-Road Premium","Limited","TRD Pro"],
  "Honda|Accord":        ["LX","Sport","Sport-L","EX-L","Touring","Hybrid Sport","Hybrid Sport-L","Hybrid Sport Touring","Hybrid Touring"],
  "Honda|Civic":         ["LX","Sport","EX","Sport Touring","Touring","Si","Type R"],
  "Honda|CR-V":          ["LX","EX","EX-L","Sport","Sport-L","Sport Touring","Hybrid Sport","Hybrid Sport-L","Hybrid Sport Touring"],
  "Honda|Pilot":         ["LX","EX","Sport","EX-L","TrailSport","Touring","Elite","Black Edition"],
  "Honda|Odyssey":       ["LX","EX","EX-L","Touring","Elite"],
  "Honda|HR-V":          ["LX","Sport","EX","EX-L"],
  "Honda|Ridgeline":     ["Sport","RTL","RTL-E","Black Edition"],
  "Ford|F-150":          ["XL","XLT","Lariat","King Ranch","Platinum","Limited","Raptor","Raptor R","Tremor"],
  "Ford|Explorer":       ["Base","XLT","ST-Line","Timberline","ST","Limited","Platinum","King Ranch"],
  "Ford|Escape":         ["Base","Active","ST-Line","ST-Line Select","ST-Line Elite","Plug-In Hybrid"],
  "Ford|Bronco":         ["Base","Big Bend","Black Diamond","Outer Banks","Badlands","Wildtrak","Everglades","Raptor"],
  "Ford|Bronco Sport":   ["Base","Big Bend","Outer Banks","Badlands","Heritage","Heritage Limited"],
  "Ford|Mustang":        ["EcoBoost","EcoBoost Premium","GT","GT Premium","Mach 1","Dark Horse","Shelby GT500"],
  "Ford|Mustang Mach-E": ["Select","California Route 1","Premium","GT","GT Performance Edition"],
  "Ford|Maverick":       ["XL","XLT","Lariat","Tremor"],
  "Ford|Ranger":         ["XL","XLT","Lariat","Raptor","Ranger Raptor"],
  "Ford|Expedition":     ["XL","XLT","Limited","Timberline","King Ranch","Platinum","Max XL","Max XLT","Max Limited","Max King Ranch","Max Platinum"],
  "Chevrolet|Silverado 1500": ["WT","Custom","Custom Trail Boss","LT","LT Trail Boss","RST","LTZ","Trail Boss","High Country","ZR2"],
  "Chevrolet|Equinox":   ["LS","LT","RS","Premier"],
  "Chevrolet|Traverse":  ["LS","LT","RS","LT Cloth","Premier","High Country"],
  "Chevrolet|Tahoe":     ["LS","LT","RST","Z71","Premier","High Country"],
  "Chevrolet|Suburban":  ["LS","LT","RST","Z71","Premier","High Country"],
  "Chevrolet|Colorado":  ["WT","LT","Z71","Trail Boss","ZR2"],
  "Chevrolet|Camaro":    ["LS","LT","LT1","SS","ZL1","Z/28"],
  "GMC|Sierra 1500":     ["Pro","SLE","Elevation","SLT","AT4","Denali","Denali Ultimate","AT4X"],
  "GMC|Yukon":           ["SLE","SLT","AT4","Denali","Denali Ultimate"],
  "GMC|Terrain":         ["SLE","SLT","AT4","Denali"],
  "GMC|Acadia":          ["SLE","SLT","AT4","Denali"],
  "Jeep|Wrangler":               ["Sport","Sport S","Willys","Sahara","High Altitude","Rubicon","Rubicon 392","4xe"],
  "Jeep|Wrangler Unlimited":     ["Sport","Sport S","Willys","Sahara","High Altitude","Rubicon","Rubicon 392","4xe"],
  "Jeep|Grand Cherokee":         ["Laredo","Altitude","Limited","Overland","Summit","Trailhawk","Summit Reserve","4xe"],
  "Jeep|Grand Cherokee L":       ["Laredo","Altitude","Limited","Overland","Summit","Trailhawk","Summit Reserve"],
  "Jeep|Cherokee":               ["Sport","Latitude","Latitude Plus","Trailhawk","Limited","Overland","High Altitude"],
  "Jeep|Compass":                ["Sport","Latitude","Latitude Lux","Trailhawk","Limited","High Altitude"],
  "Jeep|Gladiator":              ["Sport","Sport S","Willys","Texas Trail","Rubicon","Mojave","High Altitude"],
  "Ram|1500":            ["Tradesman","Big Horn","Lone Star","Laramie","Sport","Rebel","Limited","TRX","Warlock"],
  "Ram|2500":            ["Tradesman","Big Horn","Lone Star","Laramie","Power Wagon","Limited"],
  "Ram|3500":            ["Tradesman","Big Horn","Lone Star","Laramie","Limited"],
  "Subaru|Outback":      ["Base","Premium","Onyx Edition","Limited","Touring","Wilderness"],
  "Subaru|Forester":     ["Base","Premium","Sport","Limited","Touring","Wilderness"],
  "Subaru|Crosstrek":    ["Base","Premium","Sport","Limited","Wilderness"],
  "Subaru|Ascent":       ["Base","Premium","Onyx Edition","Limited","Touring"],
  "Subaru|Impreza":      ["Base","Premium","Sport","Limited"],
  "Subaru|WRX":          ["Base","Premium","Limited","GT","WRX Series.White"],
  "BMW|3 Series":        ["330i","330i xDrive","330e","M340i","M340i xDrive","M3","M3 Competition","M3 CS"],
  "BMW|5 Series":        ["530i","530i xDrive","540i","540i xDrive","550e","M550i xDrive","M5","M5 Competition"],
  "BMW|X3":              ["sDrive30i","xDrive30i","xDrive30e","M40i","M Competition"],
  "BMW|X5":              ["sDrive40i","xDrive40i","xDrive50e","M60i","X5 M","X5 M Competition"],
  "BMW|X7":              ["xDrive40i","xDrive50i","M60i","Alpina XB7"],
  "Mercedes-Benz|C-Class":  ["C 300","C 300 4MATIC","AMG C 43","AMG C 43 4MATIC","AMG C 63 S E Performance"],
  "Mercedes-Benz|E-Class":  ["E 350","E 350 4MATIC","AMG E 53","AMG E 53 4MATIC","AMG E 63 S"],
  "Mercedes-Benz|GLC":      ["GLC 300","GLC 300 4MATIC","AMG GLC 43","AMG GLC 63 S E Performance"],
  "Mercedes-Benz|GLE":      ["GLE 350","GLE 350 4MATIC","GLE 450 4MATIC","AMG GLE 53","AMG GLE 63 S"],
  "Mercedes-Benz|GLS":      ["GLS 450","GLS 450 4MATIC","AMG GLS 63"],
  "Mercedes-Benz|S-Class":  ["S 500","S 580","AMG S 63","Maybach S 580","Maybach S 680"],
  "Hyundai|Tucson":      ["SE","SEL","N Line","XRT","Limited","Hybrid Blue","Hybrid SEL","Hybrid N Line","Hybrid Limited","PHEV SEL","PHEV Limited"],
  "Hyundai|Santa Fe":    ["SE","SEL","XRT","Limited","Calligraphy","Hybrid SEL","Hybrid Limited","Hybrid Calligraphy","PHEV SEL","PHEV Limited"],
  "Hyundai|Sonata":      ["SE","SEL","SEL Plus","N Line","Limited","Hybrid Blue","Hybrid SEL","Hybrid Limited"],
  "Hyundai|Elantra":     ["SE","SEL","N Line","Limited","N","Hybrid Blue","Hybrid SEL","Hybrid Limited"],
  "Hyundai|Palisade":    ["SE","SEL","XRT","Limited","Calligraphy"],
  "Kia|Telluride":       ["LX","S","EX","SX","SX-Prestige","X-Line","X-Pro","X-Pro Prestige"],
  "Kia|Sportage":        ["LX","EX","X-Line","SX","SX Prestige","Hybrid EX","Hybrid X-Line","PHEV EX","PHEV SX Prestige"],
  "Kia|Sorento":         ["LX","S","EX","SX","SX-Prestige","X-Line","Hybrid EX","Hybrid SX Prestige","PHEV EX","PHEV SX Prestige"],
  "Kia|K5":              ["LX","GT-Line","EX","GT","GT-Line AWD"],
  "Kia|Carnival":        ["LX","EX","SX","SX Prestige","HiLimousine"],
  "Nissan|Rogue":        ["S","SV","SL","Platinum","Rock Creek"],
  "Nissan|Pathfinder":   ["S","SV","Rock Creek","SL","Platinum"],
  "Nissan|Altima":       ["S","SR","SV","SL","Platinum","SR Midnight Edition"],
  "Nissan|Frontier":     ["King Cab S","King Cab SV","Crew Cab S","Crew Cab SV","Pro-4X","PRO-X","SL","Hardbody"],
  "Nissan|Murano":       ["S","SV","SL","Platinum"],
  "Nissan|Armada":       ["S","SV","SL","Platinum"],
  "Tesla|Model 3":       ["Standard Range","Long Range","Long Range AWD","Performance"],
  "Tesla|Model Y":       ["Standard Range","Long Range","Long Range AWD","Performance"],
  "Tesla|Model S":       ["Long Range","Plaid"],
  "Tesla|Model X":       ["Long Range","Plaid"],
  "Tesla|Cybertruck":    ["Rear-Wheel Drive","All-Wheel Drive","Cyberbeast"],
  "Volkswagen|Tiguan":   ["S","SE","SE R-Line","SE R-Line Black","SEL","SEL R-Line","SEL Premium"],
  "Volkswagen|Atlas":    ["S","SE","SE Technology","SEL","SEL Premium"],
  "Volkswagen|Jetta":    ["S","Sport","SE","SEL","GLI S","GLI Autobahn"],
  "Volkswagen|Golf":     ["S","SE","Golf R","GTI S","GTI SE","GTI Autobahn"],
  "Volkswagen|ID.4":     ["Standard","Pro","Pro S","Pro S Plus","AWD Pro","AWD Pro S","AWD Pro S Plus"],
  "Mazda|CX-5":          ["Sport","Select","Carbon Edition","Preferred","Preferred Plus","Grand Touring","Grand Touring Reserve","Signature"],
  "Mazda|CX-50":         ["Select","Preferred","Premium","Premium Plus","Meridian Edition","Turbo Premium","Turbo Premium Plus"],
  "Mazda|CX-90":         ["Select","Preferred","Premium","Premium Plus","Turbo Select","Turbo Preferred","Turbo Premium","Turbo Premium Plus","Turbo S Premium Plus","PHEV Premium","PHEV Premium Plus"],
  "Mazda|Mazda3":        ["2.0 S","2.5 S","2.5 S Carbon Edition","2.5 S Select","2.5 S Preferred","2.5 S Premium","2.5 Turbo Premium","2.5 Turbo Premium Plus","Mazda3 Sedan Select","Mazda3 Sedan Premium"],
  "Lexus|RX":            ["RX 350","RX 350 F Sport","RX 350h","RX 450h","RX 450h+","RX 500h F Sport Performance"],
  "Lexus|NX":            ["NX 250","NX 350","NX 350h","NX 450h+","NX 350 F Sport","NX 350h F Sport"],
  "Lexus|GX":            ["GX 460","GX 460 Premium","GX 460 Luxury","GX 550 Premium","GX 550 Overtrail","GX 550 Luxury"],
  "Lexus|IS":            ["IS 300","IS 300 AWD","IS 350","IS 350 AWD","IS 350 F Sport","IS 500 F Sport Performance"],
  "Lexus|ES":            ["ES 250","ES 300h","ES 350","ES 350 F Sport"],
  "Acura|MDX":           ["Base","Technology","A-Spec","A-Spec SH-AWD","Type S","Type S Advance"],
  "Acura|RDX":           ["Base","Technology","A-Spec","PMC Edition","Advance"],
  "Acura|TLX":           ["Base","Technology","A-Spec","A-Spec SH-AWD","Type S","Type S Advance"],
  "Infiniti|QX60":       ["Pure","Luxe","Sensory","Autograph"],
  "Infiniti|QX80":       ["Luxe","Premium Select","Sensory","Autograph"],
  "Infiniti|Q50":        ["Pure","Luxe","Sensory","Red Sport 400"],
};

function getTrims(make, model) {
  if (!make || !model) return null;
  return VEHICLE_TRIMS[`${make}|${model}`] || null;
}

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA",
  "HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
  "DC","PR","GU","VI","AS","MP",
];

const CAR_COLORS = [
  "Black","White","Silver","Gray","Red","Blue","Dark Blue",
  "Navy","Green","Dark Green","Brown","Beige","Tan","Gold",
  "Yellow","Orange","Purple","Maroon","Champagne","Pearl White",
  "Midnight Black","Glacier White","Titanium","Bronze","Copper",
  "Other",
];

const VEHICLE_COLOR_SWATCHES = [
  { label: "White", value: "White", color: "#f8fafc", border: "#cbd5e1" },
  { label: "Silver", value: "Silver", color: "#cbd5e1", border: "#94a3b8" },
  { label: "Black", value: "Black", color: "#111827", border: "#111827" },
  { label: "Gray", value: "Gray", color: "#6b7280", border: "#6b7280" },
  { label: "Red", value: "Red", color: "#dc2626", border: "#dc2626" },
  { label: "Blue", value: "Blue", color: "#2563eb", border: "#2563eb" },
  { label: "Green", value: "Green", color: "#059669", border: "#059669" },
  { label: "Other", value: "Other", color: "linear-gradient(135deg, #f8fafc 0%, #f8fafc 48%, #2a7b6f 49%, #2a7b6f 52%, #f2eee7 53%, #f2eee7 100%)", border: "#94a3b8" },
];

const BLANK_VEHICLE = {
  nickname: "", vehicle_type: "car", year: "", make: "", model: "", trim: "",
  vin: "", color: "", license_plate: "", state: "",
  purchase_date: "", purchase_price: "", current_mileage: "", recall_count: 0, complaint_count: 0, notes: "", photo_url: "",
};

const BLANK_SERVICE = {
  service_type: "oil_change", service_date: "", mileage_at_service: "",
  shop_name: "", cost: "", parts_cost: "", labor_cost: "",
  next_service_mileage: "", next_service_date: "",
  custom_interval_miles: "", custom_interval_months: "",
  notes: "",
};

const BLANK_FUEL = {
  fill_date: "", odometer_reading: "", gallons_added: "", price_per_gallon: "",
  total_cost: "", station_name: "", station_location: "", is_full_fill: true, notes: "",
};

const BLANK_INSURANCE = {
  provider_name: "", policy_number: "", coverage_type: "", deductible: "",
  monthly_premium: "", policy_start: "", policy_expiry: "",
  roadside_assistance: false, rental_coverage: false, notes: "",
};

const BLANK_REGISTRATION = {
  registration_number: "", state: "", expiry_date: "", annual_fee: "",
  smog_required: false, smog_due_date: "", notes: "",
};

const VERDICT_CONFIG = {
  "Low":       { cls: "vq-low",       label: "Below market" },
  "Fair":      { cls: "vq-fair",      label: "Fair price"   },
  "High":      { cls: "vq-high",      label: "High"         },
  "Very High": { cls: "vq-very-high", label: "Very High"    },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function vehicleIcon(type) {
  return (VEHICLE_TYPES.find(t => t.value === type) || VEHICLE_TYPES[0]).icon;
}

function isVehiclePhoto(value) {
  return typeof value === "string" && (
    value.startsWith("data:image/") ||
    value.startsWith("https://") ||
    value.startsWith("http://")
  );
}

function compressVehicleImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        const maxDimension = 900;
        const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
        const width = Math.max(1, Math.round(image.width * scale));
        const height = Math.max(1, Math.round(image.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext("2d");
        if (!context) {
          reject(new Error("Could not process this image."));
          return;
        }
        context.drawImage(image, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.8));
      };
      image.onerror = () => reject(new Error("Could not read this image."));
      image.src = reader.result;
    };
    reader.onerror = () => reject(new Error("Could not load this file."));
    reader.readAsDataURL(file);
  });
}

function VehiclePhotoUpload({ photoUrl, onChange }) {
  const fileRef = useRef(null);
  const [uploadError, setUploadError] = useState("");

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError("");
    try {
      const compressed = await compressVehicleImage(file);
      onChange(compressed);
    } catch (err) {
      setUploadError(err.message || "Could not prepare that image.");
    } finally {
      e.target.value = "";
    }
  }

  return (
    <div className="hm-veh-photo-upload">
      <div className="hm-veh-photo-preview">
        {isVehiclePhoto(photoUrl)
          ? <img src={photoUrl} alt="Vehicle" />
          : <span>{vehicleIcon("car")}</span>}
      </div>
      <div className="hm-veh-photo-copy">
        <span>Vehicle photo</span>
        <p>Add the actual vehicle so your garage and header feel personal.</p>
        <div className="hm-veh-photo-actions">
          <button type="button" className="hm-vault-manual-btn" onClick={() => fileRef.current?.click()}>
            Upload photo
          </button>
          {photoUrl && (
            <button type="button" className="hm-vault-cancel-btn" onClick={() => onChange("")}>
              Remove
            </button>
          )}
        </div>
        {uploadError && <div className="hm-inline-error">{uploadError}</div>}
      </div>
      <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
    </div>
  );
}

function useSheetHandleDrag(onClose, threshold = 46) {
  const sheetRef = useRef(null);
  const dragRef = useRef({ active: false, startY: 0, currentY: 0 });

  const startDrag = useCallback((clientY) => {
    dragRef.current = { active: true, startY: clientY, currentY: 0 };
    if (sheetRef.current) sheetRef.current.style.transition = "none";
  }, []);

  const moveDrag = useCallback((clientY, event) => {
    if (!dragRef.current.active || !sheetRef.current) return;
    const delta = Math.max(0, clientY - dragRef.current.startY);
    dragRef.current.currentY = delta;
    if (delta > 4) event.preventDefault();
    sheetRef.current.style.transform = `translateY(${delta}px)`;
  }, []);

  const finishDrag = useCallback(() => {
    if (!dragRef.current.active || !sheetRef.current) return;
    const delta = dragRef.current.currentY;
    dragRef.current = { active: false, startY: 0, currentY: 0 };
    if (delta > threshold) {
      sheetRef.current.style.transition = "transform .2s cubic-bezier(0.4, 0, 1, 1)";
      sheetRef.current.style.transform = "translateY(100%)";
      window.setTimeout(onClose, 205);
      return;
    }
    sheetRef.current.style.transition = "transform .18s cubic-bezier(0.2, 0, 0, 1)";
    sheetRef.current.style.transform = "translateY(0)";
    window.setTimeout(() => {
      if (!sheetRef.current || dragRef.current.active) return;
      sheetRef.current.style.transform = "";
      sheetRef.current.style.transition = "";
    }, 190);
  }, [onClose, threshold]);

  const onPointerDown = useCallback((event) => {
    startDrag(event.clientY);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [startDrag]);

  const onPointerMove = useCallback((event) => {
    moveDrag(event.clientY, event);
  }, [moveDrag]);

  const onPointerUp = useCallback((event) => {
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    finishDrag();
  }, [finishDrag]);

  const onTouchStart = useCallback((event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    startDrag(touch.clientY);
  }, [startDrag]);

  const onTouchMove = useCallback((event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    moveDrag(touch.clientY, event);
  }, [moveDrag]);

  return {
    sheetRef,
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onTouchStart,
      onTouchMove,
      onTouchEnd: finishDrag,
      onTouchCancel: finishDrag,
      role: "button",
      tabIndex: 0,
      "aria-label": "Drag down to close",
      style: { cursor: "grab", touchAction: "none" },
    },
  };
}

function Modal({ title, onClose, children }) {
  return (
    <div className="modal-shell" onClick={onClose}>
      <div className="card modal-card" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="flex-between mb-16">
          <h3 style={{ fontSize: 20 }}>{title}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>x</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ConfirmVehicleRemoveModal({ vehicleName, busy, error, onCancel, onConfirm }) {
  return (
    <ConfirmModal
      tag="⚠ Remove vehicle"
      title="Remove vehicle?"
      message={[`${vehicleName} will be removed from your garage, including its saved logs and vehicle details.`, error].filter(Boolean).join(" ")}
      confirmLabel="Remove vehicle"
      onCancel={onCancel}
      onConfirm={onConfirm}
      loading={busy}
    />
  );
}

function VehiclesStickyAsk({ contextLabel = "vehicles", vehicle = null }) {
  const prompts = ["Service due soon?", "Mileage summary", "Next oil change?"];

  function openVehiclesAsk(question = "") {
    const clean = String(question || "").trim();
    const contextualQuestion = clean
      ? `${clean} Focus on ${contextLabel}.`
      : `Ask about ${contextLabel}.`;
    window.dispatchEvent(new CustomEvent("hm_open_ask", {
      detail: {
        question: contextualQuestion,
        context: {
          module: "vehicles",
          module_label: "Vehicles",
          surface: contextLabel,
          vehicle_id: vehicle?.id || null,
          vehicle_label: contextLabel,
        },
      },
    }));
  }

  return (
    <div className="fd-food-askbar hm-veh-askbar">
      <input
        className="fd-food-askbar__input"
        placeholder="✦ Ask about your vehicles..."
        onFocus={() => openVehiclesAsk("")}
        readOnly
      />
      <div className="fd-food-askbar__chips">
        {prompts.map(q => (
          <button key={q} type="button" className="fd-food-askbar__chip" onClick={() => openVehiclesAsk(q)}>
            {q}
          </button>
        ))}
      </div>
      <button type="button" className="fd-food-askbar__button" onClick={() => openVehiclesAsk(prompts[0])}>
        ✦ Ask
      </button>
    </div>
  );
}

function fmt$(n) {
  if (n == null) return "";
  return `$${Number(n).toLocaleString()}`;
}

function fmtMoney(n) {
  if (n == null || n === "") return "";
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtMiles(n) {
  if (!n) return "";
  return `${Number(n).toLocaleString()} mi`;
}

function addMonthsIso(iso, months) {
  if (!iso || !months) return "";
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  date.setMonth(date.getMonth() + Number(months));
  return date.toISOString().slice(0, 10);
}

function monthLabel(months) {
  if (!months) return "";
  return `${months} mo`;
}

function monthsBetweenIso(startIso, endIso) {
  if (!startIso || !endIso) return "";
  const start = new Date(`${startIso}T00:00:00`);
  const end = new Date(`${endIso}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) return "";
  const days = Math.round((end - start) / 86400000);
  return String(Math.max(1, Math.round(days / 30)));
}

function parseWholeNumber(value) {
  const clean = String(value ?? "").replace(/[^\d-]/g, "");
  const parsed = parseInt(clean, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function inferOilIntervalKey(text = "") {
  const clean = String(text || "").toLowerCase();
  if (!clean) return "";
  if (clean.includes("extended") && clean.includes("synthetic")) return "extended_synthetic";
  if (clean.includes("synthetic blend") || clean.includes("semi synthetic") || clean.includes("semi-synthetic")) return "synthetic_blend";
  if (clean.includes("full synthetic") || clean.includes("fully synthetic") || clean.includes("synthetic oil")) return "full_synthetic";
  if (clean.includes("conventional")) return "conventional";
  return "";
}

function fmtComplaintCategory(raw) {
  if (!raw) return "Owner complaint";
  return raw
    .split(",")
    .map((part, i) => {
      const clean = part.trim().toLowerCase().replace(/\b\/\b/g, " / ");
      const label = clean
        .replace(/\bpower train\b/g, "powertrain")
        .replace(/\bvehicle speed control\b/g, "speed control")
        .replace(/\bfuel\/propulsion system\b/g, "fuel system")
        .replace(/\bunknown or other\b/g, "other");
      return i === 0 ? label.charAt(0).toUpperCase() + label.slice(1) : label;
    })
    .join(" · ");
}

function fmtRecallCategory(raw) {
  if (!raw) return "Recall";
  const first = String(raw).split(",")[0].trim().toLowerCase();
  const cleaned = first
    .replace(/\bair bags?\b/g, "airbag")
    .replace(/\bservice brakes\b/g, "brakes")
    .replace(/\belectrical system\b/g, "electrical")
    .replace(/\bpower train\b/g, "powertrain");
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function yearsOwnedLabel(purchaseDate) {
  if (!purchaseDate) return "-";
  const start = new Date(`${purchaseDate}T00:00:00`);
  if (Number.isNaN(start.getTime())) return "-";
  const now = new Date();
  let years = now.getFullYear() - start.getFullYear();
  const beforeAnniversary =
    now.getMonth() < start.getMonth() ||
    (now.getMonth() === start.getMonth() && now.getDate() < start.getDate());
  if (beforeAnniversary) years -= 1;
  if (years <= 0) return "< 1 yr";
  return `${years} yr${years === 1 ? "" : "s"}`;
}

function vehicleFleetSummary(vehicles) {
  return vehicles.reduce((summary, vehicle) => ({
    totalMiles: summary.totalMiles + (Number(vehicle.current_mileage) || 0),
    totalRecalls: summary.totalRecalls + (Number(vehicle.recall_count) || 0),
    complaintCount: summary.complaintCount + (Number(vehicle.complaint_count) || 0),
  }), { totalMiles: 0, totalRecalls: 0, complaintCount: 0 });
}

function downloadTextFile(filename, text, mime = "text/plain") {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ── VIN Lookup ────────────────────────────────────────────────────────────────

function VinLookup({ onDecoded }) {
  const [vin, setVin]           = useState("");
  const [loading, setLoading]   = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError]       = useState("");
  const [scanNotice, setScanNotice] = useState("");
  const [matched, setMatched]   = useState(false);
  const lastLookupRef = useRef("");

  const lookup = useCallback(async (vinVal) => {
    const v = (vinVal || vin).trim();
    if (v.length !== 17) { setScanNotice(""); setError("VIN must be exactly 17 characters."); return; }
    if (loading || lastLookupRef.current === v) return;
    lastLookupRef.current = v;
    setLoading(true); setError(""); setScanNotice("");
    try {
      const data = await vehiclesApi.decodeVin(v);
      if (data.error) {
        lastLookupRef.current = "";
        setError(data.error);
        return;
      }
      onDecoded(data);
      setMatched(true);
    } catch (err) {
      lastLookupRef.current = "";
      setError(err.message || "VIN lookup failed");
    } finally {
      setLoading(false);
    }
  }, [loading, onDecoded, vin]);

  useEffect(() => {
    const v = vin.trim();
    if (v.length === 17 && v !== lastLookupRef.current && !loading) {
      lookup(v);
    }
  }, [vin, loading, lookup]);

  useEffect(() => {
    if (!scanNotice) return undefined;
    const timer = window.setTimeout(() => setScanNotice(""), 6000);
    return () => window.clearTimeout(timer);
  }, [scanNotice]);

  async function handleBarcodeFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true); setError(""); setScanNotice("");
    try {
      // Use BarcodeDetector API if available (Chrome/Edge/Android)
      if ("BarcodeDetector" in window) {
        const img  = await createImageBitmap(file);
        const bd   = new window.BarcodeDetector({ formats: ["code_39","code_128","qr_code","data_matrix"] });
        const codes = await bd.detect(img);
        const vinCode = codes.find(c => /^[A-HJ-NPR-Z0-9]{17}$/.test(c.rawValue));
        if (vinCode) {
          const v = vinCode.rawValue;
          setVin(v);
          await lookup(v);
          return;
        }
        setScanNotice("No VIN barcode found. You can type the VIN manually or try another photo.");
      } else {
        // Fallback: read as data URL and use canvas to display — user will need to type
        setScanNotice("Barcode scanning is not supported here. Please type the VIN manually.");
      }
    } catch (err) {
      setScanNotice("Could not scan barcode. Try another photo or type the VIN manually.");
    } finally {
      setScanning(false);
      e.target.value = "";
    }
  }

  return (
    <div className="hm-vin-lookup">
      {loading && <SavingNotice title="Decoding VIN" message="Looking up vehicle details..." />}
      <p className="hm-vin-label">VIN <em>optional</em></p>
      <div className="hm-vin-row">
        <input
          className="hm-auth-input hm-vin-input"
          placeholder="Enter VIN to auto-fill"
          value={vin}
          maxLength={17}
          onChange={e => { setVin(e.target.value.toUpperCase()); setError(""); setScanNotice(""); setMatched(false); }}
          onKeyDown={e => e.key === "Enter" && lookup()}
        />
        <button type="button" className="hm-vin-btn" onClick={() => lookup()} disabled={loading || vin.length !== 17}>
          {loading ? "Looking up…" : "Decode VIN"}
        </button>
        <label className="hm-vin-scan-btn" title="Scan VIN barcode with camera">
          {scanning ? "Scanning…" : "Scan VIN barcode →"}
        <input type="file" accept="image/*" capture="environment" style={{ display: "none" }}
            onChange={handleBarcodeFile} disabled={scanning} />
        </label>
      </div>
      {matched && <div className="hm-vin-match-banner">✦ VIN matched — Make, Model, Year, Trim pre-filled below</div>}
      {scanNotice && (
        <div className="hm-vin-scan-notice">
          <span>{scanNotice}</span>
          <button type="button" onClick={() => setScanNotice("")} aria-label="Dismiss VIN scan message">×</button>
        </div>
      )}
      {error && <div className="hm-inline-error" style={{ marginTop: 6 }}>{error}</div>}
    </div>
  );
}

// ── Add Vehicle Form ──────────────────────────────────────────────────────────

function AddVehicleForm({ onSaved, onCancel }) {
  const [form, setForm]   = useState(BLANK_VEHICLE);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState("");
  const [step, setStep]   = useState(1);
  const [customColor, setCustomColor] = useState("");
  const presentation = useFormPresentation();
  const showSaving = useMinLoad(busy, 700);
  const sheetDrag = useSheetHandleDrag(onCancel);

  function setField(k, v) { setForm(f => ({ ...f, [k]: v })); }
  const selectedColorIsOther = form.color === "Other" || (!!form.color && !VEHICLE_COLOR_SWATCHES.some(c => c.value === form.color));

  function chooseColor(value) {
    if (value === "Other") {
      setCustomColor(selectedColorIsOther && form.color !== "Other" ? form.color : customColor);
      setField("color", customColor || "Other");
      return;
    }
    setCustomColor("");
    setField("color", value);
  }

  function handleDecoded(data) {
    setForm(f => {
      // Normalise make: if NHTSA returns e.g. "TOYOTA", match our casing
      const rawMake  = data.make || f.make || "";
      const rawModel = data.model || f.model || "";
      const matchedMake = MAKE_LIST.find(m => m.toLowerCase() === rawMake.toLowerCase()) || rawMake;
      // Keep model as-is from NHTSA (it's specific enough)
      return {
        ...f,
        year:         data.year  ? String(data.year) : f.year,
        make:         matchedMake,
        model:        rawModel,
        trim:         data.trim  || f.trim,
        vin:          data.vin   || f.vin,
        vehicle_type: data.vehicle_type || f.vehicle_type,
        recall_count: data.recall_count ?? f.recall_count,
        complaint_count: data.complaint_count ?? f.complaint_count,
      };
    });
  }

  async function saveVehicle({ skipDetails = false } = {}) {
    if (!form.make && !form.model && !form.nickname) { setError("Enter at least a make, model, or nickname."); return; }
    setBusy(true); setError("");
    try {
      const payload = {
        ...form,
        make:           form.make   === "__other__"  ? "" : form.make,
        model:          form.model  === "__other__"  ? "" : form.model,
        trim:           form.trim   === "__manual__" ? "" : form.trim,
        color:          form.color === "Other" ? "" : form.color,
        year:           form.year           ? parseInt(form.year, 10)     : null,
        purchase_price: skipDetails ? null : form.purchase_price ? parseFloat(form.purchase_price) : null,
        current_mileage:skipDetails ? null : form.current_mileage? parseInt(form.current_mileage, 10) : null,
        purchase_date:  skipDetails ? "" : form.purchase_date,
        license_plate:  skipDetails ? "" : form.license_plate,
        state:          skipDetails ? "" : form.state,
        notes:          skipDetails ? "" : form.notes,
      };
      const created = await vehiclesApi.create(payload);
      onSaved(created);
    } catch (err) {
      setError(err.message || "Could not save vehicle");
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (step === 1) {
      setError("");
      setStep(2);
      return;
    }
    await saveVehicle();
  }

  const trimOpts = getTrims(form.make, form.model);
  const title = "Add to garage";
  function goNext(e) {
    e?.preventDefault?.();
    setError("");
    setStep(2);
  }

  const makeField = (
    <label className="hm-veh-sheet-field">
      <span>Make</span>
      <select className="hm-auth-input" value={form.make}
        onChange={e => setForm(f => ({ ...f, make: e.target.value, model: "", trim: "" }))}>
        <option value="">Select make…</option>
        {MAKE_LIST.map(m => <option key={m} value={m}>{m}</option>)}
        <option value="__other__">Other / Not listed</option>
      </select>
    </label>
  );

  const modelField = (
    <label className="hm-veh-sheet-field">
      <span>Model</span>
      {(form.make && form.make !== "__other__" && VEHICLE_MAKES[form.make]) ? (
        <select className="hm-auth-input" value={form.model}
          onChange={e => setForm(f => ({ ...f, model: e.target.value, trim: "" }))}>
          <option value="">Select model…</option>
          {VEHICLE_MAKES[form.make].map(m => <option key={m} value={m}>{m}</option>)}
          <option value="__other__">Other / Not listed</option>
        </select>
      ) : (
        <input className="hm-auth-input" placeholder="Model name…"
          value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value, trim: "" }))} />
      )}
    </label>
  );

  const yearField = (
    <label className="hm-veh-sheet-field">
      <span>Year</span>
      <select className="hm-auth-input" value={form.year}
        onChange={e => setField("year", e.target.value)}>
        <option value="">Year…</option>
        {YEAR_LIST.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </label>
  );

  const trimField = (
    <label className="hm-veh-sheet-field">
      <span>Trim</span>
      {trimOpts ? (
        <select className="hm-auth-input" value={form.trim}
          onChange={e => setField("trim", e.target.value)}>
          <option value="">Select trim…</option>
          {trimOpts.map(t => <option key={t} value={t}>{t}</option>)}
          <option value="__manual__">Other / Not listed</option>
        </select>
      ) : (
        <input className="hm-auth-input" placeholder="EX, XLE, Sport…"
          value={form.trim} onChange={e => setField("trim", e.target.value)} />
      )}
    </label>
  );

  const content = (
    <div className="hm-veh-add-sheet-content">
      {showSaving && <SavingNotice title="Saving" message="Adding vehicle to garage..." />}
      <div className="gf-steps-row hm-veh-sheet-steps">
        <span className={`gf-step-dot${step === 1 ? " gf-step-dot--active" : " gf-step-dot--done"}`} />
        <span className={`gf-step-dot${step === 2 ? " gf-step-dot--active" : ""}`} />
        <span className="gf-step-txt">STEP {step} OF 2</span>
      </div>

      {error && <div className="hm-inline-error hm-veh-sheet-error">{error}</div>}

      <form onSubmit={handleSubmit} className="hm-veh-sheet-form">
        <div className="hm-veh-sheet-body">
          {step === 1 ? (
            <>
              <div className="hm-veh-sheet-section">
                <div className="hm-veh-sheet-section-head">
                  <strong>Identity</strong>
                  <span>What vehicle is this?</span>
                </div>
                <VinLookup onDecoded={handleDecoded} />
              </div>

              <div className="hm-veh-sheet-grid hm-veh-sheet-grid--2">
                {makeField}
                {modelField}
              </div>

              <div className="hm-veh-sheet-grid hm-veh-sheet-grid--2">
                {yearField}
                {trimField}
              </div>

              <div className="hm-veh-sheet-field">
                <span>Color</span>
                <div className="hm-veh-color-swatches">
                  {VEHICLE_COLOR_SWATCHES.map(color => {
                    const active = color.value === "Other" ? selectedColorIsOther : form.color === color.value;
                    return (
                      <button
                        key={color.value}
                        type="button"
                        className={active ? "is-active" : ""}
                        style={{ "--veh-color": color.color, "--veh-color-border": color.border }}
                        onClick={() => chooseColor(color.value)}
                        aria-label={color.label}
                        title={color.label}
                      />
                    );
                  })}
                </div>
                {selectedColorIsOther && (
                  <input className="hm-auth-input hm-veh-color-other" placeholder="Custom color"
                    value={form.color === "Other" ? customColor : form.color}
                    onChange={e => { setCustomColor(e.target.value); setField("color", e.target.value || "Other"); }} />
                )}
              </div>

              <div className="hm-veh-sheet-field">
                <span>Vehicle type</span>
                <div className="hm-veh-type-chips">
                  {VEHICLE_TYPES.filter(t => ["car", "truck", "suv", "motorcycle", "van", "other"].includes(t.value)).map(t => (
                    <button
                      key={t.value}
                      type="button"
                      className={form.vehicle_type === t.value ? "is-active" : ""}
                      onClick={() => setField("vehicle_type", t.value)}
                    >
                      <span>{t.icon}</span>{t.label}
                    </button>
                  ))}
                </div>
              </div>

              <label className="hm-veh-sheet-field">
                <span>Nickname <em>optional</em></span>
                <input className="hm-auth-input" placeholder="e.g. Daily driver, Family hauler"
                  value={form.nickname} onChange={e => setField("nickname", e.target.value)} />
              </label>
            </>
          ) : (
            <>
              <div className="hm-veh-optional-tip">
                <strong>💡 Optional details</strong>
                <span>These help HouseMind track value, costs, and registration. Skip anything you don't have handy.</span>
              </div>

              <div className="hm-veh-sheet-grid hm-veh-sheet-grid--2">
                <label className="hm-veh-sheet-field">
                  <span>Current mileage</span>
                  <input className="hm-auth-input" placeholder="e.g. 42000" type="number" min="0"
                    value={form.current_mileage} onChange={e => setField("current_mileage", e.target.value)} />
                </label>

                <label className="hm-veh-sheet-field">
                  <span>Purchase price</span>
                  <input className="hm-auth-input" placeholder="e.g. 28500" type="number" min="0"
                    value={form.purchase_price} onChange={e => setField("purchase_price", e.target.value)} />
                </label>
              </div>

              <label className="hm-veh-sheet-field">
                <span>Purchase date</span>
                <input className="hm-auth-input" type="date"
                  value={form.purchase_date} onChange={e => setField("purchase_date", e.target.value)} />
              </label>

              <div className="hm-veh-sheet-grid hm-veh-sheet-grid--2">
                <label className="hm-veh-sheet-field">
                  <span>License plate</span>
                  <input className="hm-auth-input" placeholder="ABC 1234"
                    value={form.license_plate} onChange={e => setField("license_plate", e.target.value)} />
                </label>

                <label className="hm-veh-sheet-field">
                  <span>State</span>
                  <select className="hm-auth-input" value={form.state}
                    onChange={e => setField("state", e.target.value)}>
                    <option value="">Select state…</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
              </div>

              <label className="hm-veh-sheet-field">
                <span>Notes</span>
                <textarea className="hm-auth-input" rows={3} placeholder="Anything else to remember about this vehicle"
                  value={form.notes} onChange={e => setField("notes", e.target.value)} />
              </label>

              <button type="button" className="hm-veh-skip-details" onClick={() => saveVehicle({ skipDetails: true })} disabled={busy}>
                Add without details →
              </button>
            </>
          )}
        </div>

        <div className="hm-veh-sheet-footer">
          <button type="button" className="hm-veh-sheet-link" onClick={step === 1 ? onCancel : () => setStep(1)}>
            {step === 1 ? "Cancel" : "← Back"}
          </button>
          {step === 1 ? (
            <button type="button" className="hm-veh-sheet-primary" onClick={goNext}>
              Next →
            </button>
          ) : (
            <button type="submit" className="hm-veh-sheet-primary" disabled={busy}>
              {busy ? "Saving…" : "Save vehicle"}
            </button>
          )}
        </div>
      </form>
    </div>
  );

  if (presentation === "modal") {
    return (
      <CenteredModal title={title} onClose={onCancel}>
        {content}
      </CenteredModal>
    );
  }

  return (
    <div className="hm-gifts-sheet-overlay hm-veh-add-sheet-overlay" onClick={onCancel}>
      <section
        ref={sheetDrag.sheetRef}
        className="gf-add-person-sheet hm-veh-add-sheet"
        onClick={event => event.stopPropagation()}
        aria-label={title}
      >
        <div className="hm-gifts-sheet-handle" {...sheetDrag.handleProps} />
        <SheetLogoRow title={title} onClose={onCancel} closeLabel="Close add to garage" />
        {content}
      </section>
    </div>
  );
}

// ── Service Log Tab ───────────────────────────────────────────────────────────

function ServiceLogTab({ vehicle, onMileageUpdate, initialServiceType, onInitialServiceHandled, onSavedFromMaintenance }) {
  const { applyAiUsage } = useAuth();
  const [logs, setLogs]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [form, setForm]             = useState(BLANK_SERVICE);
  const [, setAdditionalTypes] = useState([]);
  const [selectedComboKey, setSelectedComboKey] = useState("");
  const [editServiceTarget, setEditServiceTarget] = useState(null);
  const [busy, setBusy]             = useState(false);
  const [error, setError]           = useState("");
  const [success, setSuccess]       = useState("");
  const [scanning, setScanning]     = useState(false);
  const [deleteBusyId, setDeleteBusyId] = useState(null);
  const [scanPreview, setScanPreview] = useState(null); // { confidence, ... scanned fields }
  const [scanOpen, setScanOpen]     = useState(false);
  const [scanConsent, setScanConsent] = useState(false);
  const [serviceActionSheet, setServiceActionSheet] = useState(null);
  const [expandedMilestones, setExpandedMilestones] = useState({});
  const [confirmDeleteService, setConfirmDeleteService] = useState(null);
  const [confirmDeleteMilestone, setConfirmDeleteMilestone] = useState(null);
  const [oilInterval, setOilInterval] = useState("full_synthetic");
  const [customOilMiles, setCustomOilMiles] = useState("");
  const [customOilMonths, setCustomOilMonths] = useState("");
  const [serviceLogFilter, setServiceLogFilter] = useState("all");
  const [openServiceYears, setOpenServiceYears] = useState({});
  const receiptUploadRef = useRef(null);
  const receiptCameraRef = useRef(null);
  const showWorking = useMinLoad(busy || scanning || !!deleteBusyId, 700);
  const closeServiceSheet = useCallback(() => {
    setShowForm(false);
    setScanOpen(false);
    setScanConsent(false);
    setScanPreview(null);
    setForm(BLANK_SERVICE);
    setAdditionalTypes([]);
    setSelectedComboKey("");
    setEditServiceTarget(null);
    setOilInterval("full_synthetic");
    setCustomOilMiles("");
    setCustomOilMonths("");
    setError("");
  }, []);
  const serviceSheetDrag = useSheetHandleDrag(closeServiceSheet, 54);
  const selectedCombo = SERVICE_COMBOS.find(combo => combo.key === selectedComboKey);
  const selectedServiceLabel = selectedCombo
    ? selectedCombo.label
    : form.service_type
      ? SERVICE_TYPE_LABELS[form.service_type] || "Single service"
      : "";
  const selectedServiceTypes = editServiceTarget?.kind === "visit"
    ? (editServiceTarget.items || [])
    : selectedCombo
      ? selectedCombo.types
      : form.service_type
        ? [form.service_type]
        : [];
  const includesOilChange = selectedServiceTypes.includes("oil_change");
  const oilDue = (() => {
    const preset = OIL_INTERVAL_PRESETS.find(item => item.key === oilInterval) || OIL_INTERVAL_PRESETS[0];
    const miles = oilInterval === "custom" ? parseInt(customOilMiles, 10) : preset.miles;
    const months = oilInterval === "custom" ? parseInt(customOilMonths, 10) : preset.months;
    const serviceMileage = parseWholeNumber(form.mileage_at_service);
    const nextMileage = Number.isFinite(serviceMileage) && Number.isFinite(miles) && miles > 0
      ? serviceMileage + miles
      : null;
    const nextDate = Number.isFinite(months) && months > 0
      ? addMonthsIso(form.service_date, months)
      : "";
    return {
      label: preset.label,
      miles: Number.isFinite(miles) && miles > 0 ? miles : null,
      months: Number.isFinite(months) && months > 0 ? months : null,
      nextMileage,
      nextDate,
      shouldSave: Boolean(nextMileage || nextDate),
    };
  })();

  function resetOilInterval() {
    setOilInterval("full_synthetic");
    setCustomOilMiles("");
    setCustomOilMonths("");
  }

  function oilDueHint() {
    if (!oilDue.shouldSave) return "Using the vehicle maintenance schedule for this oil change.";
    const parts = [];
    if (oilDue.nextMileage) parts.push(fmtMiles(oilDue.nextMileage));
    if (oilDue.nextDate) parts.push(`by ${fmtDate(oilDue.nextDate)}`);
    else if (oilDue.months) parts.push(monthLabel(oilDue.months));
    const dueText = parts.join(" or ");
    return `Next change at ${dueText}${parts.length > 1 ? " · whichever comes first" : ""}`;
  }

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await vehiclesApi.listServiceGrouped(vehicle.id);
      setLogs(data || []);
    } catch {}
    setLoading(false);
  }, [vehicle.id]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  useEffect(() => {
    if (!initialServiceType) return;
    setScanOpen(false);
    setScanPreview(null);
    if (initialServiceType === MANUAL_SERVICE_ENTRY) {
      setForm({ ...BLANK_SERVICE, service_type: "" });
      setAdditionalTypes([]);
      setSelectedComboKey("");
    } else {
      setForm({ ...BLANK_SERVICE, service_type: initialServiceType });
      setAdditionalTypes([]);
      setSelectedComboKey("");
    }
    resetOilInterval();
    setShowForm(true);
    setError("");
    onInitialServiceHandled?.();
  }, [initialServiceType, onInitialServiceHandled]);

  function setField(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function openReceiptScan() {
    setShowForm(false);
    setScanPreview(null);
    setScanConsent(false);
    setScanOpen(true);
    setError("");
  }

  function openManualEntry() {
    setScanOpen(false);
    setScanPreview(null);
    setForm({ ...BLANK_SERVICE, service_type: "" });
    setAdditionalTypes([]);
    setSelectedComboKey("");
    setEditServiceTarget(null);
    resetOilInterval();
    setShowForm(true);
    setError("");
  }

  async function handleReceiptFile(file) {
    if (!file) return;
    setScanning(true); setError(""); setScanPreview(null);
    try {
      const result = await vehiclesApi.scanReceipt(vehicle.id, file);
      applyAiUsage?.(result?.ai_usage);
      if (!result) { setError("Scan returned no data."); return; }
      if (result.error === "ai_not_configured") { setError("AI not configured — add an API key in Settings to enable receipt scanning."); return; }
      if (result.error) { setError(result.message || "Scan failed."); return; }
      // Pre-fill form and show confirmation preview
      setScanPreview(result);
      const scannedMileage = parseWholeNumber(result.mileage_at_service);
      const fallbackMileage = scannedMileage || parseWholeNumber(vehicle.current_mileage);
      setForm({
        service_type:         result.service_type         || "other",
        service_date:         result.service_date         || "",
        mileage_at_service:   fallbackMileage ? String(fallbackMileage) : "",
        shop_name:            result.shop_name            || "",
        cost:                 result.cost                 ? String(result.cost) : "",
        parts_cost:           result.parts_cost           ? String(result.parts_cost) : "",
        labor_cost:           result.labor_cost           ? String(result.labor_cost) : "",
        next_service_mileage: result.next_service_mileage ? String(result.next_service_mileage) : "",
        next_service_date:    result.next_service_date    || "",
        custom_interval_miles: result.custom_interval_miles ? String(result.custom_interval_miles) : "",
        custom_interval_months: result.custom_interval_months ? String(result.custom_interval_months) : "",
        notes:                result.notes                || "",
      });
      setSelectedComboKey("");
      setAdditionalTypes([]);
      if (result.service_type === "oil_change") {
        const intervalMiles = Number(result.custom_interval_miles || 0);
        const intervalMonths = Number(result.custom_interval_months || 0);
        const serviceMiles = parseWholeNumber(result.mileage_at_service) || 0;
        const nextMiles = parseWholeNumber(result.next_service_mileage) || 0;
        const derivedMonths = intervalMonths ? String(intervalMonths) : monthsBetweenIso(result.service_date, result.next_service_date);
        const inferredInterval = inferOilIntervalKey([
          result.oil_type,
          result.oil_interval_type,
          result.service_description,
          result.notes,
        ].filter(Boolean).join(" "));
        if (!intervalMiles && !intervalMonths && inferredInterval) {
          setOilInterval(inferredInterval);
          setCustomOilMiles("");
          setCustomOilMonths("");
        } else if (intervalMiles || intervalMonths || (serviceMiles && nextMiles && nextMiles > serviceMiles) || derivedMonths) {
          setOilInterval("custom");
          setCustomOilMiles(intervalMiles ? String(intervalMiles) : serviceMiles && nextMiles && nextMiles > serviceMiles ? String(nextMiles - serviceMiles) : "");
          setCustomOilMonths(derivedMonths);
        } else {
          resetOilInterval();
        }
      } else {
        resetOilInterval();
      }
      setShowForm(true);
      setScanOpen(false);
    } catch (err) {
      setError(err.message || "Receipt scan failed");
    } finally {
      setScanning(false);
    }
  }

  async function handleReceiptInput(e) {
    const file = e.target.files?.[0];
    await handleReceiptFile(file);
    e.target.value = "";
  }

  async function handleAdd(e) {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const combo = SERVICE_COMBOS.find(item => item.key === selectedComboKey);
      const selectedTypes = combo ? combo.types : [form.service_type];
      const basePayload = {
        ...form,
        mileage_at_service:   parseWholeNumber(form.mileage_at_service),
        cost:                 form.cost                  ? parseFloat(form.cost)                    : null,
        parts_cost:           form.parts_cost            ? parseFloat(form.parts_cost)              : null,
        labor_cost:           form.labor_cost            ? parseFloat(form.labor_cost)              : null,
        next_service_mileage: parseWholeNumber(form.next_service_mileage),
        next_service_date:    form.next_service_date || null,
        custom_interval_miles: form.custom_interval_miles ? parseInt(form.custom_interval_miles, 10) : null,
        custom_interval_months: form.custom_interval_months ? parseInt(form.custom_interval_months, 10) : null,
      };
      const withOilDue = payload => ({
        ...payload,
        next_service_mileage: oilDue.shouldSave ? oilDue.nextMileage : null,
        next_service_date: oilDue.shouldSave ? oilDue.nextDate : null,
        custom_interval_miles: oilInterval === "custom" && oilDue.miles ? oilDue.miles : null,
        custom_interval_months: oilInterval === "custom" && oilDue.months ? oilDue.months : null,
      });
      const payloadForType = (payload, type) => type === "oil_change" ? withOilDue(payload) : payload;
      if (editServiceTarget) {
        if (editServiceTarget.kind === "milestone") {
          await vehiclesApi.updateMilestone(vehicle.id, editServiceTarget.milestone_id, {
            performed_date: form.service_date || null,
            performed_at_miles: basePayload.mileage_at_service,
            shop: form.shop_name || "",
            total_cost: basePayload.cost,
            notes: form.notes || "",
          });
          await loadLogs();
          setForm(BLANK_SERVICE);
          setAdditionalTypes([]);
          setSelectedComboKey("");
          setEditServiceTarget(null);
          resetOilInterval();
          setShowForm(false);
          setScanPreview(null);
          setSuccess("Milestone updated.");
          setTimeout(() => setSuccess(""), 3000);
          onSavedFromMaintenance?.();
          return;
        }
        const targetIds = editServiceTarget.log_ids?.length ? editServiceTarget.log_ids : [editServiceTarget.id];
        const targetItems = editServiceTarget.kind === "visit" ? (editServiceTarget.items || []) : [];
        for (let i = 0; i < targetIds.filter(Boolean).length; i += 1) {
          const id = targetIds.filter(Boolean)[i];
          const targetType = editServiceTarget.kind === "visit" ? targetItems[i] : basePayload.service_type;
          let editPayload = editServiceTarget.kind === "visit"
            ? Object.fromEntries(Object.entries(basePayload).filter(([key]) => key !== "service_type"))
            : basePayload;
          editPayload = payloadForType(editPayload, targetType);
          await vehiclesApi.updateService(vehicle.id, id, editPayload);
        }
        await loadLogs();
        setForm(BLANK_SERVICE);
        setAdditionalTypes([]);
        setSelectedComboKey("");
        setEditServiceTarget(null);
        resetOilInterval();
        setShowForm(false);
        setScanPreview(null);
        setSuccess("Service log updated.");
        setTimeout(() => setSuccess(""), 3000);
        onSavedFromMaintenance?.();
        return;
      }
      const visitId = selectedTypes.length > 1 && typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : selectedTypes.length > 1
          ? `${Date.now()}-${Math.random().toString(16).slice(2)}`
          : null;
      for (let i = 0; i < selectedTypes.length; i += 1) {
        const type = selectedTypes[i];
        const siblingLabels = selectedTypes.filter(other => other !== type).map(other => SERVICE_TYPE_LABELS[other] || other);
        const comboNote = siblingLabels.length ? `Logged with: ${siblingLabels.join(", ")}` : "";
        const payload = {
          ...basePayload,
          service_type: type,
          notes: [form.notes?.trim(), comboNote].filter(Boolean).join("\n"),
          visit_id: visitId,
        };
        await vehiclesApi.addService(vehicle.id, payloadForType(payload, type));
      }
      await loadLogs();
      // Update vehicle mileage if this log has a higher odometer
      if (basePayload.mileage_at_service && (!vehicle.current_mileage || basePayload.mileage_at_service > vehicle.current_mileage)) {
        onMileageUpdate(vehicle.id, basePayload.mileage_at_service);
      }
      setForm(BLANK_SERVICE);
      setAdditionalTypes([]);
      setSelectedComboKey("");
      resetOilInterval();
      setShowForm(false);
      setScanPreview(null);
      setSuccess(selectedTypes.length > 1 ? "Service logs added." : "Service log added.");
      setTimeout(() => setSuccess(""), 3000);
      onSavedFromMaintenance?.();
    } catch (err) {
      setError(err.message || "Could not add service log");
    } finally {
      setBusy(false);
    }
  }

  function selectServiceCombo(combo) {
    const [primary, ...rest] = combo.types;
    setSelectedComboKey(combo.key);
    setForm(prev => ({ ...prev, service_type: primary || prev.service_type }));
    setAdditionalTypes(rest);
  }

  function openEditService(target) {
    setServiceActionSheet(null);
    setScanOpen(false);
    setScanPreview(null);
    setSelectedComboKey("");
    setAdditionalTypes([]);
    setEditServiceTarget(target);
    setForm({
      ...BLANK_SERVICE,
      service_type: target.service_type || target.items?.[0] || "other",
      service_date: target.service_date || "",
      mileage_at_service: target.mileage_at_service ? String(target.mileage_at_service) : "",
      shop_name: target.shop_name || "",
      cost: target.cost != null ? String(target.cost) : "",
      notes: target.notes || "",
    });
    const oilNext = target.next_items?.find(item => item.service_type === "oil_change") || target;
    if ((target.service_type === "oil_change" || target.items?.includes("oil_change")) && (oilNext.next_service_mileage || oilNext.next_service_date)) {
      const baseMiles = parseWholeNumber(target.mileage_at_service) || 0;
      const nextMiles = parseWholeNumber(oilNext.next_service_mileage) || 0;
      const intervalMiles = Number(oilNext.custom_interval_miles || 0);
      const intervalMonths = Number(oilNext.custom_interval_months || 0);
      const savedMiles = intervalMiles || (baseMiles && nextMiles && nextMiles > baseMiles ? nextMiles - baseMiles : 0);
      const matchedPreset = OIL_INTERVAL_PRESETS.find(preset => (
        preset.key !== "custom" &&
        preset.key !== "manufacturer" &&
        preset.miles === savedMiles
      ));
      const inferredInterval = inferOilIntervalKey([
        target.oil_type,
        oilNext.oil_type,
        target.notes,
        oilNext.notes,
        target.service_description,
        oilNext.service_description,
      ].filter(Boolean).join(" "));
      if (intervalMiles || intervalMonths) {
        setOilInterval("custom");
        setCustomOilMiles(savedMiles ? String(savedMiles) : "");
        setCustomOilMonths(intervalMonths ? String(intervalMonths) : "");
      } else if (matchedPreset) {
        setOilInterval(matchedPreset.key);
        setCustomOilMiles("");
        setCustomOilMonths("");
      } else if (inferredInterval) {
        setOilInterval(inferredInterval);
        setCustomOilMiles("");
        setCustomOilMonths("");
      } else {
        setOilInterval("custom");
        setCustomOilMiles(savedMiles ? String(savedMiles) : "");
        setCustomOilMonths("");
      }
    } else {
      resetOilInterval();
    }
    setShowForm(true);
    setError("");
  }

  function openServiceActions(target) {
    setServiceActionSheet(target);
  }

  function transitionToEditService(target) {
    setServiceActionSheet(null);
    window.setTimeout(() => openEditService(target), 0);
  }

  const serviceLogRows = (() => {
    if (logs.some(row => row.type)) {
      return logs.map((row, index) => {
        if (row.type === "milestone") {
          return {
            kind: "milestone",
            key: `milestone-${row.milestone_id || row.label || index}-${row.date || ""}-${row.mileage || ""}`,
            milestone_id: row.milestone_id,
            milestone_miles: row.milestone_miles,
            source_label: row.label || "Milestone service",
            service_date: row.date,
            mileage_at_service: row.mileage,
            shop_name: row.shop,
            cost: row.total_cost,
            logs: (row.items || []).map(service_type => ({ service_type })),
          };
        }
        if (row.type === "manual_combo") {
          return {
            kind: "visit",
            key: `visit-${row.visit_id || index}`,
            visit_id: row.visit_id,
            log_ids: row.log_ids || [],
            items: row.items || [],
            service_date: row.date,
            mileage_at_service: row.mileage,
            shop_name: row.shop,
            cost: row.cost,
            notes: row.notes,
            next_items: row.next_items || [],
          };
        }
        return {
          kind: "log",
          key: `log-${row.log_id || index}`,
          log: {
            id: row.log_id,
            service_type: row.service_type,
            service_date: row.date,
            mileage_at_service: row.mileage,
            shop_name: row.shop,
            cost: row.cost,
            next_service_mileage: row.next_service_mileage,
            next_service_date: row.next_service_date,
            custom_interval_miles: row.custom_interval_miles,
            custom_interval_months: row.custom_interval_months,
            notes: row.notes,
          },
        };
      });
    }
    const sorted = [...logs].sort((a, b) => {
      const aTime = Date.parse(a.service_date || a.created_at || "") || 0;
      const bTime = Date.parse(b.service_date || b.created_at || "") || 0;
      return bTime - aTime;
    });
    const milestoneGroups = new Map();
    const rows = [];
    sorted.forEach(log => {
      if (log.source === "milestone") {
        const key = `${log.source_label || "Milestone service"}|${log.service_date || ""}|${log.mileage_at_service || ""}`;
        if (!milestoneGroups.has(key)) {
          const group = {
            kind: "milestone",
            key,
            source_label: log.source_label || "Milestone service",
            service_date: log.service_date,
            mileage_at_service: log.mileage_at_service,
            shop_name: log.shop_name,
            cost: log.cost,
            logs: [],
          };
          milestoneGroups.set(key, group);
          rows.push(group);
        }
        milestoneGroups.get(key).logs.push(log);
      } else {
        rows.push({ kind: "log", key: `log-${log.id}`, log });
      }
    });
    return rows;
  })();

  function serviceRowDateValue(row) {
    return row.kind === "milestone" || row.kind === "visit" ? row.service_date : row.log?.service_date;
  }

  function serviceRowYear(row) {
    const value = serviceRowDateValue(row);
    if (!value) return "No date";
    const parsed = new Date(`${value}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? "No date" : String(parsed.getFullYear());
  }

  function serviceRowTypes(row) {
    if (row.kind === "milestone") return (row.logs || []).map(log => log.service_type);
    if (row.kind === "visit") return row.items || [];
    return row.log?.service_type ? [row.log.service_type] : [];
  }

  function serviceTypeMatchesFilter(type, filterKey = serviceLogFilter) {
    if (filterKey === "all") return true;
    if (filterKey === "oil") return type === "oil_change";
    if (filterKey === "tires") return ["tire_rotation", "tire_replacement", "alignment"].includes(type);
    if (filterKey === "repairs") return REPAIR_SERVICE_TYPES.has(type);
    return true;
  }

  function serviceRowVisibleTypes(row) {
    const types = serviceRowTypes(row);
    if (serviceLogFilter === "all") return types;
    return types.filter(type => serviceTypeMatchesFilter(type));
  }

  function serviceRowMatchesFilter(row) {
    const types = serviceRowTypes(row);
    if (serviceLogFilter === "all") return true;
    if (serviceLogFilter === "oil") return types.some(type => serviceTypeMatchesFilter(type, "oil"));
    if (serviceLogFilter === "tires") return types.some(type => serviceTypeMatchesFilter(type, "tires"));
    if (serviceLogFilter === "repairs") return types.some(type => serviceTypeMatchesFilter(type, "repairs"));
    return true;
  }

  const filteredServiceRows = serviceLogRows.filter(serviceRowMatchesFilter);
  const visibleServiceRows = filteredServiceRows.slice(0, 5);
  const collapsedServiceRows = filteredServiceRows.slice(5);
  const SERVICE_YEAR_DISPLAY_LIMIT = 10;
  const serviceLogSections = collapsedServiceRows.reduce((sections, row) => {
    const year = serviceRowYear(row);
    let section = sections.find(item => item.year === year);
    if (!section) {
      section = { year, rows: [] };
      sections.push(section);
    }
    section.rows.push(row);
    return sections;
  }, []);

  function isServiceYearOpen(year) {
    if (Object.prototype.hasOwnProperty.call(openServiceYears, year)) return openServiceYears[year];
    return false;
  }

  function toggleServiceYear(year) {
    setOpenServiceYears(prev => ({
      ...prev,
      [year]: !isServiceYearOpen(year),
    }));
  }

  function renderServiceLogRow(row) {
    return row.kind === "milestone" || row.kind === "visit" ? (
      <article className={`hm-veh-log-item ${row.kind === "milestone" ? "hm-veh-log-item--milestone" : "hm-veh-log-item--visit"}`} key={row.key}>
        {(() => {
          const itemLabels = Array.from(new Set(row.kind === "visit"
            ? serviceRowVisibleTypes(row).map(type => SERVICE_TYPE_LABELS[type] || type)
            : serviceRowVisibleTypes(row).map(type => SERVICE_TYPE_LABELS[type] || type)));
          const expanded = !!expandedMilestones[row.key];
          const shownLabels = expanded ? itemLabels : itemLabels.slice(0, 2);
          const hiddenCount = Math.max(0, itemLabels.length - shownLabels.length);
          const shownText = shownLabels.join(", ");
          return (
            <>
              <div className="hm-veh-log-type">{row.kind === "visit" ? "Service visit" : row.source_label}</div>
              <div className="hm-veh-log-meta">
                {row.service_date && <span>{fmtDate(row.service_date)}</span>}
                {row.mileage_at_service && <span>{fmtMiles(row.mileage_at_service)}</span>}
                {row.shop_name && <span>{row.shop_name}</span>}
                {row.cost != null && <span className="hm-veh-log-cost">{fmt$(row.cost)}</span>}
              </div>
              <div className="hm-veh-log-bundle">
                {shownText && <span>{shownText}</span>}
                {hiddenCount > 0 && <button type="button" onClick={() => setExpandedMilestones(prev => ({ ...prev, [row.key]: true }))}>+{hiddenCount} more ↓</button>}
                {expanded && itemLabels.length > 2 && <button type="button" onClick={() => setExpandedMilestones(prev => ({ ...prev, [row.key]: false }))}>Collapse ↑</button>}
              </div>
              {row.kind === "visit" && (() => {
                const oilNext = (row.next_items || []).find(item => item.service_type === "oil_change");
                return oilNext ? (
                  <div className="hm-veh-log-next">
                    Next oil change: {oilNext.next_service_mileage ? fmtMiles(oilNext.next_service_mileage) : ""}{oilNext.next_service_mileage && oilNext.next_service_date ? " · " : ""}{oilNext.next_service_date ? fmtDate(oilNext.next_service_date) : ""}
                  </div>
                ) : null;
              })()}
              {row.milestone_id ? (
                <div className="hm-veh-row-menu">
                  <button type="button" onClick={() => openServiceActions({ kind: "milestone", title: row.source_label, target: row })} aria-label="Milestone actions">···</button>
                </div>
              ) : row.kind === "visit" && (
                <div className="hm-veh-row-menu">
                  <button type="button" onClick={() => openServiceActions({ kind: "visit", title: "Service visit", target: row, deleteTarget: { id: row.log_ids?.[0], ids: row.log_ids, kind: "visit" } })} aria-label="Service visit actions">···</button>
                </div>
              )}
            </>
          );
        })()}
      </article>
    ) : (
      <article className="hm-veh-log-item" key={row.key}>
        {(() => {
          const log = row.log;
          return (
            <>
              <div className="hm-veh-log-type">{SERVICE_TYPE_LABELS[log.service_type] || log.service_type}</div>
              <div className="hm-veh-log-meta">
                {log.service_date && <span>{fmtDate(log.service_date)}</span>}
                {log.mileage_at_service && <span>{fmtMiles(log.mileage_at_service)}</span>}
                {log.shop_name && <span>{log.shop_name}</span>}
                {log.cost != null && <span className="hm-veh-log-cost">{fmt$(log.cost)}</span>}
              </div>
              {(log.next_service_mileage || log.next_service_date) && (
                <div className="hm-veh-log-next">
                  Next: {log.next_service_mileage ? `${fmtMiles(log.next_service_mileage)}` : ""}{log.next_service_mileage && log.next_service_date ? " · " : ""}{log.next_service_date ? fmtDate(log.next_service_date) : ""}
                </div>
              )}
              <div className="hm-veh-row-menu">
                <button type="button" onClick={() => openServiceActions({ kind: "log", title: SERVICE_TYPE_LABELS[log.service_type] || log.service_type, target: { kind: "log", ...log }, deleteTarget: log })} aria-label="Service log actions">···</button>
              </div>
            </>
          );
        })()}
      </article>
    );
  }

  async function deleteLog(id) {
    setDeleteBusyId(id);
    setError("");
    try {
      await vehiclesApi.deleteService(vehicle.id, id);
      await loadLogs();
    } catch (err) {
      setError(err.message || "Could not delete log");
    } finally {
      setDeleteBusyId(null);
    }
  }

  return (
    <div className="hm-veh-tab-content">
      {showWorking && <SavingNotice title={scanning ? "Scanning" : deleteBusyId ? "Removing" : "Saving"} message={scanning ? "Reading service receipt..." : deleteBusyId ? "Removing service log..." : "Saving service log..."} />}
      {confirmDeleteService && (
        <ConfirmModal
          tag="Service history"
          title="Delete service log?"
          message="This service record will be removed from your vehicle history."
          confirmLabel="Delete log"
          onCancel={() => setConfirmDeleteService(null)}
          onConfirm={async () => {
            const target = confirmDeleteService;
            setConfirmDeleteService(null);
            for (const id of (target.ids?.length ? target.ids : [target.id]).filter(Boolean)) {
              await deleteLog(id);
            }
          }}
          loading={deleteBusyId === confirmDeleteService.id}
        />
      )}
      {confirmDeleteMilestone && (
        <ConfirmModal
          tag="Milestone service"
          title="Delete milestone?"
          message="This removes the milestone record and its generated service log items."
          confirmLabel="Delete milestone"
          onCancel={() => setConfirmDeleteMilestone(null)}
          onConfirm={async () => {
            const target = confirmDeleteMilestone;
            setConfirmDeleteMilestone(null);
            setDeleteBusyId(`milestone-${target.milestone_id}`);
            try {
              await vehiclesApi.deleteMilestone(vehicle.id, target.milestone_id);
              await loadLogs();
            } catch (err) {
              setError(err.message || "Could not delete milestone");
            } finally {
              setDeleteBusyId(null);
            }
          }}
          loading={deleteBusyId === `milestone-${confirmDeleteMilestone.milestone_id}`}
        />
      )}
      {serviceActionSheet && (
        <div className="hm-veh-action-modal-overlay" onClick={() => setServiceActionSheet(null)}>
          <section className="hm-signout-modal hm-veh-action-modal" onClick={event => event.stopPropagation()} aria-label="Service actions">
            <div className="hm-veh-action-modal-head">
              <div>
                <strong>{serviceActionSheet.kind === "milestone" ? "Milestone actions" : "Service actions"}</strong>
                <span>{serviceActionSheet.title || "Service history"}</span>
              </div>
              <button type="button" onClick={() => setServiceActionSheet(null)} aria-label="Close actions">×</button>
            </div>
            <div className="hm-veh-action-modal-body">
              {serviceActionSheet.kind === "milestone" ? (
                <>
                  <button type="button" onClick={() => transitionToEditService({ kind: "milestone", ...serviceActionSheet.target })}>Edit milestone</button>
                  <button type="button" className="is-danger" onClick={() => {
                    setConfirmDeleteMilestone(serviceActionSheet.target);
                    setServiceActionSheet(null);
                  }}>Delete milestone</button>
                </>
              ) : (
                <>
                  <button type="button" onClick={() => transitionToEditService(serviceActionSheet.target)}>Edit entry</button>
                  <button type="button" className="is-danger" onClick={() => {
                    setConfirmDeleteService(serviceActionSheet.deleteTarget);
                    setServiceActionSheet(null);
                  }}>Delete entry</button>
                </>
              )}
            </div>
          </section>
        </div>
      )}
      {error   && <div className="hm-inline-error"   style={{ marginBottom: 10 }}>{error}</div>}
      {success && <div className="hm-inline-success" style={{ marginBottom: 10 }}>{success}</div>}

      <div className="hm-veh-tab-head hm-veh-service-head">
        <h3>🔧 Service history</h3>
        {!showForm && (
          <div className="hm-veh-tab-actions hm-veh-service-actions">
            <button type="button" className="hm-veh-scan-receipt-btn" title="Scan a service receipt or PDF" onClick={openReceiptScan}>
              <span className="hm-veh-scan-action-icon">📷</span>
              <span>{scanning ? "Scanning…" : "Scan receipt"}</span>
            </button>
            <button type="button" className="hm-veh-add-service-btn" onClick={openManualEntry}>
              <span>+</span> Add manually
            </button>
          </div>
        )}
      </div>

      <input ref={receiptUploadRef} className="hm-veh-file-input" type="file" accept="image/*,application/pdf" onChange={handleReceiptInput} disabled={scanning} />
      <input ref={receiptCameraRef} className="hm-veh-file-input" type="file" accept="image/*" capture="environment" onChange={handleReceiptInput} disabled={scanning} />

      {(scanOpen || showForm) && (
        <div className="hm-gifts-sheet-overlay hm-veh-service-sheet-overlay" onClick={closeServiceSheet}>
      <BackButtonTrap onClose={closeServiceSheet} />
          <section
            ref={serviceSheetDrag.sheetRef}
            className="gf-add-person-sheet hm-veh-service-sheet"
            onClick={event => event.stopPropagation()}
            aria-label={showForm ? "Add service log" : "Scan service receipt"}
          >
            <div className="hm-gifts-sheet-handle" {...serviceSheetDrag.handleProps} />
            <div className="hm-veh-service-sheet-head">
              <h2>{showForm ? (editServiceTarget ? "Edit service log" : "Add service log") : "Scan receipt"}</h2>
              <button type="button" onClick={closeServiceSheet} aria-label="Close service sheet">×</button>
            </div>

            {scanOpen && !showForm && (
              <div className="hm-veh-service-sheet-body hm-veh-service-scan-body">
                <div className="hm-veh-service-sheet-copy">
                  Upload a clear photo or text-based PDF of a service receipt or repair invoice. HouseMind will always show you a review screen before saving anything.
                </div>
                <label className="hm-veh-scan-consent">
                  <input type="checkbox" checked={scanConsent} onChange={e => setScanConsent(e.target.checked)} />
                  <span>HouseMind will read this receipt only to extract vehicle service details. Review everything before saving.</span>
                </label>
                <div className={`hm-veh-service-drop${!scanConsent ? " is-disabled" : ""}`}>
                  <span className="hm-veh-service-drop-icon">📸</span>
                  <strong>Upload receipt or invoice</strong>
                  <span>Photo or text-based PDF — details will be shown for your review before saving.</span>
                  <div className="hm-veh-service-drop-actions">
                    <button type="button" onClick={() => receiptUploadRef.current?.click()} disabled={!scanConsent || scanning}>
                      Upload file
                    </button>
                    <button type="button" onClick={() => receiptCameraRef.current?.click()} disabled={!scanConsent || scanning}>
                      Take photo
                    </button>
                  </div>
                </div>
                <div className="hm-veh-service-detected">
                  <h3>Detected service details</h3>
                  <div>Upload a receipt to review extracted service details here.</div>
                </div>
              </div>
            )}

            {showForm && (
              <form onSubmit={handleAdd} className="hm-veh-service-sheet-body hm-veh-service-sheet-form">
                {scanPreview && (
                  <div className="hm-veh-scan-preview hm-veh-service-sheet-preview">
                    <span className="hm-veh-scan-preview-icon">✓</span>
                    <span>Receipt scanned. Confidence: <strong>{scanPreview.confidence || "medium"}</strong>. Review before saving.</span>
                  </div>
                )}
                {!editServiceTarget && !scanPreview && (
                  <div className="hm-veh-service-mode-card hm-veh-service-pick-card">
                    <strong>What type of service?</strong>
                    <div className="hm-veh-service-combo-grid">
                      {SERVICE_COMBOS.map(combo => (
                        <button
                          key={combo.key}
                          type="button"
                          className={selectedComboKey === combo.key ? "is-selected" : ""}
                          onClick={() => selectServiceCombo(combo)}
                        >
                          {combo.label}
                        </button>
                      ))}
                    </div>
                    {!selectedComboKey && (
                      <>
                        <div className="hm-veh-service-or">or a single service</div>
                        <div className="hm-veh-sheet-field">
                          <select
                            className="hm-auth-input"
                            value={form.service_type}
                            onChange={e => {
                              setSelectedComboKey("");
                              setAdditionalTypes([]);
                              setField("service_type", e.target.value);
                            }}
                          >
                            <option value="">Choose service…</option>
                            {Object.entries(SERVICE_TYPE_LABELS).map(([v, l]) => (
                              <option key={v} value={v}>{l}</option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}
                    {selectedComboKey && (
                      <button
                        type="button"
                        className="hm-veh-service-change-btn"
                        onClick={() => { setSelectedComboKey(""); setAdditionalTypes([]); setField("service_type", ""); }}
                      >
                        Change to single service
                      </button>
                    )}
                  </div>
                )}
                {!editServiceTarget && selectedServiceLabel && (
                  <div className="hm-veh-selected-service">
                    <span>{scanPreview ? "Detected service" : "Selected"}</span>
                    {!scanPreview && (
                      <>
                        <strong>{selectedServiceLabel}</strong>
                        {selectedCombo && <small>{selectedCombo.types.map(type => SERVICE_TYPE_LABELS[type] || type).join(", ")}</small>}
                      </>
                    )}
                    {scanPreview ? (
                      <div className="hm-veh-detected-service-edit">
                        <select
                          className="hm-auth-input"
                          value={selectedComboKey ? `combo:${selectedComboKey}` : form.service_type}
                          onChange={e => {
                            const value = e.target.value;
                            if (value.startsWith("combo:")) {
                              const combo = SERVICE_COMBOS.find(item => item.key === value.replace("combo:", ""));
                              if (combo) selectServiceCombo(combo);
                              return;
                            }
                            setSelectedComboKey("");
                            setAdditionalTypes([]);
                            setField("service_type", value);
                          }}
                        >
                          {SERVICE_COMBOS.map(combo => (
                            <option key={combo.key} value={`combo:${combo.key}`}>{combo.label}</option>
                          ))}
                          {Object.entries(SERVICE_TYPE_LABELS).map(([v, l]) => (
                            <option key={v} value={v}>{l}</option>
                          ))}
                        </select>
                      </div>
                    ) : null}
                  </div>
                )}
                {(selectedComboKey || form.service_type || editServiceTarget) && (
                  <>
                <div className="hm-veh-sheet-grid hm-veh-sheet-grid--2">
                  <label className="hm-veh-sheet-field">
                    <span>Date</span>
                    <input type="date" className="hm-auth-input" value={form.service_date}
                      onChange={e => setField("service_date", e.target.value)} />
                  </label>
                  <label className="hm-veh-sheet-field">
                    <span>Mileage</span>
                    <input type="number" min="0" className="hm-auth-input" placeholder="69250"
                      value={form.mileage_at_service} onChange={e => setField("mileage_at_service", e.target.value)} />
                  </label>
                </div>
                {includesOilChange && (
                  <div className="hm-veh-oil-interval-card">
                    <label className="hm-veh-sheet-field hm-veh-oil-select">
                      <span>Oil type</span>
                      <select className="hm-auth-input" value={oilInterval} onChange={e => setOilInterval(e.target.value)}>
                        {OIL_INTERVAL_PRESETS.map(preset => (
                          <option key={preset.key} value={preset.key}>{preset.label}</option>
                        ))}
                      </select>
                    </label>
                    {oilInterval === "custom" && (
                      <div className="hm-veh-oil-custom-box">
                        <strong>Set your own interval</strong>
                        <div className="hm-veh-sheet-grid hm-veh-sheet-grid--2">
                          <label className="hm-veh-sheet-field">
                            <span>Miles until next change</span>
                            <input type="number" min="1" className="hm-auth-input" placeholder="5000"
                              value={customOilMiles} onChange={e => setCustomOilMiles(e.target.value)} />
                          </label>
                          <label className="hm-veh-sheet-field">
                            <span>Months until next change</span>
                            <input type="number" min="1" className="hm-auth-input" placeholder="6"
                              value={customOilMonths} onChange={e => setCustomOilMonths(e.target.value)} />
                          </label>
                        </div>
                      </div>
                    )}
                    <div className="hm-veh-oil-preview">{oilDueHint()}</div>
                  </div>
                )}
                <label className="hm-veh-sheet-field">
                  <span>Shop / location <em>optional</em></span>
                  <input className="hm-auth-input" placeholder="e.g. Jiffy Lube, Dealership"
                    value={form.shop_name} onChange={e => setField("shop_name", e.target.value)} />
                </label>
                <label className="hm-veh-sheet-field">
                  <span>Total cost <em>optional</em></span>
                  <input type="number" min="0" step="0.01" className="hm-auth-input" placeholder="79.99"
                    value={form.cost} onChange={e => setField("cost", e.target.value)} />
                </label>
                <label className="hm-veh-sheet-field">
                  <span>Notes <em>optional</em></span>
                  <textarea className="hm-auth-input" rows={2} placeholder="Parts used, technician notes…"
                    value={form.notes} onChange={e => setField("notes", e.target.value)} />
                </label>
                  </>
                )}
                <div className="hm-veh-service-sheet-footer">
                  <button type="button" className="hm-veh-sheet-link" onClick={closeServiceSheet}>Cancel</button>
                  <button type="submit" className="hm-veh-sheet-primary" disabled={busy || (!selectedComboKey && !form.service_type && !editServiceTarget)}>{busy ? "Saving…" : editServiceTarget ? "Save changes" : "Save service"}</button>
                </div>
              </form>
            )}
          </section>
        </div>
      )}

      {loading ? (
        <div className="hm-vault-empty">Loading…</div>
      ) : logs.length === 0 ? (
        !showForm && !scanOpen ? (
          <div className="hm-veh-tab-empty hm-veh-service-empty">
            <div className="hm-veh-tab-empty-icon">🔧</div>
            <strong>No service history yet</strong>
            <p>Scan a receipt or add manually to start tracking your service history.</p>
            <div className="hm-veh-service-empty-card">
              <span>What gets tracked</span>
              <ul>
                <li>Mileage, shop, service type, and cost.</li>
                <li>Next service date or odometer reminders.</li>
              </ul>
            </div>
          </div>
        ) : null
      ) : (
        <div className="hm-veh-log-history">
          <div className="hm-veh-log-filters" aria-label="Filter service history">
            {SERVICE_LOG_FILTERS.map(filter => (
              <button
                key={filter.key}
                type="button"
                className={serviceLogFilter === filter.key ? "is-active" : ""}
                onClick={() => {
                  setServiceLogFilter(filter.key);
                  setOpenServiceYears({});
                }}
              >
                {filter.label}
              </button>
            ))}
          </div>
          {filteredServiceRows.length === 0 ? (
            <div className="hm-veh-log-empty-filter">No service records match this filter.</div>
          ) : (
            <div className="hm-veh-log-list">
              {visibleServiceRows.length > 0 && (
                <div className="hm-veh-log-year-rows">
                  {visibleServiceRows.map(renderServiceLogRow)}
                </div>
              )}
              {serviceLogSections.length > 0 && (
                <div className="hm-veh-log-earlier">
                  <div className="hm-veh-log-earlier-label">Earlier history</div>
                  {serviceLogSections.map(section => {
                    const open = isServiceYearOpen(section.year);
                    return (
                      <section className={`hm-veh-log-year-section${open ? " is-open" : ""}`} key={section.year}>
                        <button type="button" className="hm-veh-log-year-toggle" onClick={() => toggleServiceYear(section.year)} aria-expanded={open}>
                          <span>{section.year}</span>
                          <em>{section.rows.length} {section.rows.length === 1 ? "record" : "records"}</em>
                          <strong>{open ? "Hide ↑" : "Show ↓"}</strong>
                        </button>
                        {open && (
                          <div className="hm-veh-log-year-rows">
                            {section.rows.slice(0, SERVICE_YEAR_DISPLAY_LIMIT).map(renderServiceLogRow)}
                            {section.rows.length > SERVICE_YEAR_DISPLAY_LIMIT && (
                              <div className="hm-veh-log-year-cap">
                                Showing latest {SERVICE_YEAR_DISPLAY_LIMIT} of {section.rows.length} records for {section.year}.
                              </div>
                            )}
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Quote Checker Tab ─────────────────────────────────────────────────────────

function QuoteCheckerTab({ vehicle }) {
  const { applyAiUsage } = useAuth();
  const [form, setForm]           = useState({ service_description: "", quoted_total: "", parts_cost: "", labor_cost: "" });
  const [result, setResult]       = useState(null);
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState("");
  const [scanning, setScanning]   = useState(false);
  const [quoteScanOpen, setQuoteScanOpen] = useState(false);
  const [scanConsent, setScanConsent] = useState(false);
  const [scanPreview, setScanPreview] = useState(null);
  const quoteUploadRef = useRef(null);
  const quoteCameraRef = useRef(null);
  const showWorking = useMinLoad(busy || scanning, 700);
  const closeQuoteScanSheet = useCallback(() => setQuoteScanOpen(false), []);
  const quoteSheetDrag = useSheetHandleDrag(closeQuoteScanSheet, 54);

  function setField(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleQuoteFile(file) {
    if (!file) return;
    setScanning(true); setError(""); setScanPreview(null); setResult(null);
    try {
      const data = await vehiclesApi.scanQuote(vehicle.id, file);
      applyAiUsage?.(data?.ai_usage);
      if (!data) { setError("Scan returned no data."); return; }
      if (data.error === "ai_not_configured") { setError("AI not configured — add an API key in Settings to enable scanning."); return; }
      if (data.error) { setError(data.message || "Scan failed."); return; }
      setScanPreview(data);
      setForm(f => ({
        ...f,
        service_description: data.service_description || f.service_description,
        quoted_total:        data.quoted_total != null ? String(data.quoted_total) : f.quoted_total,
        parts_cost:          data.parts_cost   != null ? String(data.parts_cost)   : f.parts_cost,
        labor_cost:          data.labor_cost   != null ? String(data.labor_cost)   : f.labor_cost,
      }));
      setQuoteScanOpen(false);
    } catch (err) {
      setError(err.message || "Quote scan failed");
    } finally {
      setScanning(false);
    }
  }

  async function handleQuoteScan(e) {
    const file = e.target.files?.[0];
    try {
      await handleQuoteFile(file);
    } finally {
      e.target.value = "";
    }
  }

  async function handleCheck(e) {
    e.preventDefault();
    if (!form.service_description.trim()) { setError("Describe the service to check."); return; }
    setBusy(true); setError(""); setResult(null);
    try {
      const data = await vehiclesApi.quoteCheck(vehicle.id, form);
      applyAiUsage?.(data?.ai_usage);
      if (data.error === "ai_not_configured") {
        setError("AI is not configured. Add an ANTHROPIC_API_KEY or OPENAI_API_KEY to enable quote checking.");
        return;
      }
      setResult(data);
    } catch (err) {
      setError(err.message || "Quote check failed");
    } finally {
      setBusy(false);
    }
  }

  const vc = result ? (VERDICT_CONFIG[result.verdict] || VERDICT_CONFIG["Fair"]) : null;

  return (
    <div className="hm-veh-tab-content">
      {showWorking && <SavingNotice title={scanning ? "Scanning" : "Analyzing"} message={scanning ? "Reading this quote..." : "Checking this repair quote..."} />}
      <div className="hm-veh-tab-head">
        <h3>💵 Is this quote fair?</h3>
      </div>
      <button type="button" className="hm-veh-scan-receipt-btn hm-veh-scan-receipt-btn--wide" title="Scan a quote or estimate" onClick={() => setQuoteScanOpen(true)} disabled={scanning}>
        {scanning ? "Scanning…" : "📷 Scan quote document"}
      </button>
      <input ref={quoteUploadRef} className="hm-veh-file-input" type="file" accept="image/*,application/pdf" onChange={handleQuoteScan} disabled={scanning} />
      <input ref={quoteCameraRef} className="hm-veh-file-input" type="file" accept="image/*" capture="environment" onChange={handleQuoteScan} disabled={scanning} />

      {quoteScanOpen && (
        <div className="hm-gifts-sheet-overlay hm-veh-service-sheet-overlay" onClick={closeQuoteScanSheet}>
      <BackButtonTrap onClose={closeQuoteScanSheet} />
          <section
            ref={quoteSheetDrag.sheetRef}
            className="gf-add-person-sheet hm-veh-service-sheet"
            onClick={event => event.stopPropagation()}
            aria-label="Scan quote document"
          >
            <div className="hm-gifts-sheet-handle" {...quoteSheetDrag.handleProps} />
            <div className="hm-veh-service-sheet-head">
              <h2>Scan quote</h2>
              <button type="button" onClick={closeQuoteScanSheet} aria-label="Close quote scan">×</button>
            </div>
            <div className="hm-veh-service-sheet-body hm-veh-service-scan-body">
              <div className="hm-veh-service-sheet-copy">
                Upload a clear photo or text-based PDF of a repair estimate. HouseMind will extract the service description and costs, then you review everything before checking the quote.
              </div>
              <label className="hm-veh-scan-consent">
                <input type="checkbox" checked={scanConsent} onChange={e => setScanConsent(e.target.checked)} />
                <span>HouseMind will read this estimate only to extract repair quote details. Review the numbers before running the quote check.</span>
              </label>
              <div className={`hm-veh-service-drop${!scanConsent ? " is-disabled" : ""}`}>
                <span className="hm-veh-service-drop-icon">📸</span>
                <strong>Upload repair estimate</strong>
                <span>Photo or text-based PDF — service and cost fields will be filled for review.</span>
                <div className="hm-veh-service-drop-actions">
                  <button type="button" onClick={() => quoteUploadRef.current?.click()} disabled={!scanConsent || scanning}>
                    Upload file
                  </button>
                  <button type="button" onClick={() => quoteCameraRef.current?.click()} disabled={!scanConsent || scanning}>
                    Take photo
                  </button>
                </div>
              </div>
              <div className="hm-veh-service-detected">
                <h3>Detected quote details</h3>
                <div>Upload an estimate to review extracted service, parts, labor, and total cost here.</div>
              </div>
            </div>
          </section>
        </div>
      )}

      {scanPreview && (
        <div className="hm-veh-scan-preview">
          <span className="hm-veh-scan-preview-icon">✓</span>
          <span>Quote scanned — <strong>confidence: {scanPreview.confidence || "medium"}</strong>{scanPreview.shop_name ? ` · ${scanPreview.shop_name}` : ""}. Review and confirm below.</span>
          <button type="button" className="hm-vault-cancel-btn hm-veh-scan-dismiss"
            onClick={() => setScanPreview(null)}>Dismiss</button>
        </div>
      )}

      <p className="hm-veh-quote-intro">
        Upload a photo or PDF of the estimate, or type the numbers in. AI compares against fair market price.
      </p>

      {error && <div className="hm-inline-error" style={{ marginBottom: 10 }}>{error}</div>}

      <form onSubmit={handleCheck} className="hm-veh-quote-form">
        <label className="hm-veh-form-span2">
          <span>Service description</span>
          <textarea className="hm-auth-input" rows={2}
            placeholder="e.g. Front and rear brake pads + rotors replacement"
            value={form.service_description}
            onChange={e => setField("service_description", e.target.value)} />
        </label>
        <label>
          <span>Total quoted ($) <em>auto-detected</em></span>
          <input type="number" min="0" step="0.01" className="hm-auth-input" placeholder="480"
            value={form.quoted_total} onChange={e => setField("quoted_total", e.target.value)} />
        </label>
        <label>
          <span>Parts cost ($)</span>
          <input type="number" min="0" step="0.01" className="hm-auth-input" placeholder="220"
            value={form.parts_cost} onChange={e => setField("parts_cost", e.target.value)} />
        </label>
        <label>
          <span>Labor cost ($)</span>
          <input type="number" min="0" step="0.01" className="hm-auth-input" placeholder="260"
            value={form.labor_cost} onChange={e => setField("labor_cost", e.target.value)} />
        </label>
        <div style={{ gridColumn: "1/-1" }}>
          <button type="submit" className="hm-veh-check-btn" disabled={busy}>
            {busy ? "Analyzing…" : "Check this quote"}
          </button>
        </div>
      </form>

      {result && (
        <div className="hm-veh-quote-result">
          <div className="hm-veh-quote-result-head">
            <strong>Quote analysis</strong>
            <span className={`hm-veh-verdict-pill ${vc.cls}`}>{result.verdict}</span>
          </div>
          {form.quoted_total && (
            <div className="hm-veh-quote-row">
              <span>Your quote</span>
              <strong>{fmtMoney(form.quoted_total)}</strong>
            </div>
          )}
          {(result.fair_range_low || result.fair_range_high) && (
            <div className="hm-veh-quote-row">
              <span>Fair market range</span>
              <strong>{fmt$(result.fair_range_low)} – {fmt$(result.fair_range_high)}</strong>
            </div>
          )}
          <div className="hm-veh-quote-row">
            <span>Verdict</span>
            <strong>{vc.label}</strong>
          </div>
          {result.verdict_explanation && <p className="hm-veh-quote-explain">{result.verdict_explanation}</p>}
          {result.whats_typically_included && (
            <div className="hm-veh-quote-section">
              <strong>What's typically included</strong>
              <p>{result.whats_typically_included}</p>
            </div>
          )}
          {result.red_flags?.length > 0 && (
            <div className="hm-veh-quote-section">
              <strong>Red flags to watch for</strong>
              <ul>{result.red_flags.map((f, i) => <li key={i}>{f}</li>)}</ul>
            </div>
          )}
          {result.questions_to_ask?.length > 0 && (
            <div className="hm-veh-quote-section">
              <strong>Questions to ask the shop</strong>
              <ul>{result.questions_to_ask.map((q, i) => <li key={i}>{q}</li>)}</ul>
            </div>
          )}
          {result.summary && <p className="hm-veh-quote-summary">{result.summary}</p>}
        </div>
      )}
    </div>
  );
}

// ── Maintenance Plan Tab ──────────────────────────────────────────────────────

// ── Vehicle Service Affiliate Links ───────────────────────────────────────────

function VehicleServiceLinks({ hasDIY = false }) {
  const [links, setLinks] = useState(null);

  useEffect(() => {
    affiliateApi.vehicleLinks({ service: "general", diy: hasDIY ? "true" : "false", part: "" })
      .then(setLinks).catch(() => {});
  }, [hasDIY]);

  if (!links) return null;

  const serviceOptions = [
    { key: "jiffy_lube",   label: "Jiffy Lube",    url: links.jiffy_lube },
    { key: "valvoline",    label: "Valvoline",      url: links.valvoline },
    { key: "firestone",    label: "Firestone",      url: links.firestone },
    { key: "gas_buddy",    label: "GasBuddy",       url: links.gas_buddy },
    { key: "carfax",       label: "CARFAX",         url: links.carfax },
  ].filter(o => o.url);

  const partsOptions = hasDIY ? [
    { key: "autozone",     label: "AutoZone",       url: links.autozone },
    { key: "oreilly",      label: "O'Reilly",       url: links.oreilly },
  ].filter(o => o.url) : [];

  if (!serviceOptions.length && !partsOptions.length) return null;

  return (
    <div className="hm-veh-service-links">
      {serviceOptions.length > 0 && (
        <>
          <div className="hm-veh-service-links-label">
            Book service
          </div>
          <div className="hm-veh-service-link-grid">
            {serviceOptions.map(opt => (
              <a
                key={opt.key}
                href={opt.url}
                target="_blank"
                rel="noopener noreferrer"
                className="hm-veh-service-link-chip"
                onClick={() => affiliateApi.track(opt.key, "vehicles", opt.url).catch(() => {})}
              >
                <span>{opt.label}</span>
                <span aria-hidden="true">↗</span>
              </a>
            ))}
          </div>
        </>
      )}
      {partsOptions.length > 0 && (
        <>
          <div className="hm-veh-service-links-label">
            Order parts (DIY)
          </div>
          <div className="hm-veh-service-link-grid">
            {partsOptions.map(opt => (
              <a
                key={opt.key}
                href={opt.url}
                target="_blank"
                rel="noopener noreferrer"
                className="hm-veh-service-link-chip"
                onClick={() => affiliateApi.track(opt.key, "vehicles", opt.url).catch(() => {})}
              >
                <span>{opt.label}</span>
                <span aria-hidden="true">↗</span>
              </a>
            ))}
          </div>
        </>
      )}
      <p className="affiliate-disclosure" style={{ marginTop: 10 }}>
        Some links may be affiliate links. HouseMind may earn a small commission at no extra cost to you.
      </p>
    </div>
  );
}

function MaintenanceTab({ vehicle, onLogService, onShowServiceHistory }) {
  const { applyAiUsage } = useAuth();
  const [result, setResult] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [milestoneDraft, setMilestoneDraft] = useState(null);
  const [expandedScheduleType, setExpandedScheduleType] = useState("");
  const [showOkSchedule, setShowOkSchedule] = useState(false);
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState("");
  const showWorking = useMinLoad(busy, 700);

  useEffect(() => {
    let ignore = false;
    vehiclesApi.maintenanceSchedule(vehicle.id)
      .then(data => { if (!ignore) { setSchedule(data?.schedule || []); setMilestones(data?.milestones || []); } })
      .catch(() => { if (!ignore) { setSchedule([]); setMilestones([]); } });
    return () => { ignore = true; };
  }, [vehicle.id]);

  function openMilestoneSheet(ms) {
    setMilestoneDraft({
      milestone_miles: ms.miles,
      label: ms.label,
      performed_at_miles: "",
      performed_date: new Date().toISOString().slice(0, 10),
      shop: "",
      total_cost: "",
      notes: "",
      items_completed: [...(ms.includes || [])],
      includes: ms.includes || [],
    });
  }

  function toggleMilestoneItem(item) {
    setMilestoneDraft(d => {
      if (!d) return d;
      const set = new Set(d.items_completed || []);
      set.has(item) ? set.delete(item) : set.add(item);
      return { ...d, items_completed: [...set] };
    });
  }

  async function saveMilestone(e) {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const payload = {
        ...milestoneDraft,
        performed_at_miles: milestoneDraft.performed_at_miles ? parseInt(milestoneDraft.performed_at_miles, 10) : null,
        total_cost: milestoneDraft.total_cost ? parseFloat(milestoneDraft.total_cost) : null,
      };
      const data = await vehiclesApi.addMilestone(vehicle.id, payload);
      setSchedule(data?.schedule || []);
      setMilestones(data?.milestones || []);
      setMilestoneDraft(null);
    } catch (err) {
      setError(err.message || "Could not save milestone service");
    } finally {
      setBusy(false);
    }
  }

  async function generate() {
    setBusy(true); setError(""); setResult(null);
    try {
      const data = await vehiclesApi.maintenanceAdvice(vehicle.id);
      applyAiUsage?.(data?.ai_usage);
      if (data.error === "ai_not_configured") {
        setError("Maintenance advice is temporarily unavailable.");
        return;
      }
      setResult(data);
    } catch (err) {
      setError(err.message || "Could not generate advice");
    } finally {
      setBusy(false);
    }
  }

  const scheduleStatus = {
    no_history: { cls: "hm-veh-priority--no-history", label: "—" },
    overdue: { cls: "hm-veh-priority--high", label: "Over" },
    due_soon: { cls: "hm-veh-priority--medium", label: "Soon" },
    ok: { cls: "hm-veh-priority--low", label: "OK" },
  };
  const milestoneStatus = {
    done: { cls: "hm-veh-priority--low", label: "✓ Done" },
    overdue: { cls: "hm-veh-priority--high", label: "Overdue" },
    due_soon: { cls: "hm-veh-priority--medium", label: "Due soon" },
    upcoming: { cls: "hm-veh-priority--no-history", label: "Upcoming" },
  };

  const allNoHistory = schedule.length > 0 && schedule.every(item => item.status === "no_history");
  const doneMilestones = milestones.filter(ms => ms.status === "done");
  const nextMilestone = milestones.find(ms => ms.status !== "done");
  const hasAttentionSchedule = schedule.some(item => item.status !== "ok");
  const visibleSchedule = showOkSchedule || !hasAttentionSchedule ? schedule : schedule.filter(item => item.status !== "ok");
  const hiddenOkCount = schedule.filter(item => item.status === "ok").length;

  const URGENCY_CONFIG = {
    high:   { cls: "hm-maint-high",   label: "Overdue"    },
    medium: { cls: "hm-maint-medium", label: "Due soon"   },
    low:    { cls: "hm-maint-low",    label: "Upcoming"   },
  };

  function Section({ items, urgency }) {
    if (!items?.length) return null;
    const cfg = URGENCY_CONFIG[urgency];
    return (
      <div className="hm-maint-section" aria-label={cfg.label}>
        {items.map((item, i) => (
          <div className="hm-maint-item" key={i}>
            <div className="hm-maint-item-top">
              <strong>{item.item}</strong>
              <span className={`hm-maint-status ${cfg.cls}`}>{cfg.label}</span>
              {item.estimated_cost && <span className="hm-maint-cost">{item.estimated_cost}</span>}
              {item.diy_possible && <span className="hm-maint-diy">DIY ✓</span>}
            </div>
            {item.reason && <p className="hm-maint-reason">{item.reason}</p>}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="hm-veh-tab-content">
      {showWorking && <SavingNotice title="Checking" message="Building maintenance plan..." />}
      {milestoneDraft && (
        <div className="hm-gifts-sheet-overlay hm-veh-service-sheet-overlay" onClick={() => setMilestoneDraft(null)}>
          <section className="gf-add-person-sheet hm-veh-service-sheet" onClick={event => event.stopPropagation()} aria-label="Add last milestone service">
            <div className="hm-gifts-sheet-handle" />
            <div className="hm-veh-service-sheet-head">
              <h2>Add last service</h2>
              <button type="button" onClick={() => setMilestoneDraft(null)} aria-label="Close milestone sheet">×</button>
            </div>
            <form className="hm-veh-service-sheet-body hm-veh-service-sheet-form" onSubmit={saveMilestone}>
              <div className="hm-veh-sheet-grid hm-veh-sheet-grid--2">
                <label className="hm-veh-sheet-field"><span>Mileage when done</span><input className="hm-auth-input" type="number" min="0" value={milestoneDraft.performed_at_miles} onChange={e => setMilestoneDraft(d => ({ ...d, performed_at_miles: e.target.value }))} /></label>
                <label className="hm-veh-sheet-field"><span>Date performed</span><input className="hm-auth-input" type="date" value={milestoneDraft.performed_date} onChange={e => setMilestoneDraft(d => ({ ...d, performed_date: e.target.value }))} /></label>
                <label className="hm-veh-sheet-field"><span>Shop optional</span><input className="hm-auth-input" value={milestoneDraft.shop} onChange={e => setMilestoneDraft(d => ({ ...d, shop: e.target.value }))} /></label>
                <label className="hm-veh-sheet-field"><span>Total cost optional</span><input className="hm-auth-input" type="number" min="0" step="0.01" value={milestoneDraft.total_cost} onChange={e => setMilestoneDraft(d => ({ ...d, total_cost: e.target.value }))} /></label>
              </div>
              <div className="hm-veh-sheet-field hm-veh-form-span2">
                <span>What was completed?</span>
                <div className="hm-veh-milestone-checks">
                  {milestoneDraft.includes.map(item => (
                    <label key={item}>
                      <input type="checkbox" checked={(milestoneDraft.items_completed || []).includes(item)} onChange={() => toggleMilestoneItem(item)} />
                      <span>{item}</span>
                    </label>
                  ))}
                </div>
              </div>
              <label className="hm-veh-sheet-field"><span>Notes optional</span><textarea className="hm-auth-input" rows={2} value={milestoneDraft.notes} onChange={e => setMilestoneDraft(d => ({ ...d, notes: e.target.value }))} /></label>
              <div className="hm-veh-service-sheet-footer">
                <button type="button" className="hm-veh-sheet-link" onClick={() => setMilestoneDraft(null)}>Cancel</button>
                <button type="submit" className="hm-veh-sheet-primary" disabled={busy}>{busy ? "Saving..." : "Save service"}</button>
              </div>
            </form>
          </section>
        </div>
      )}
      <div className="hm-veh-tab-head">
        <h3>🛠️ Maintenance advisor</h3>
      </div>
      <p className="hm-veh-quote-intro">
        HouseMind uses your mileage, service history, and saved intervals to build a maintenance plan — overdue, due soon, and upcoming.
      </p>

      {error && <div className="hm-inline-error" style={{ marginBottom: 10 }}>{error}</div>}

      {milestones.length > 0 && (
        <div className="hm-veh-obd-results" style={{ marginBottom: 12 }}>
          <div className="hm-veh-obd-results-label">Milestone services</div>
          {doneMilestones.length > 0 && (
            <div className="hm-veh-milestone-history-line">
              <span>Past services {doneMilestones.map(ms => `✓ ${Math.round(ms.miles / 1000)}k`).join(" · ")}</span>
              <button type="button" onClick={onShowServiceHistory}>
                See history →
              </button>
            </div>
          )}
          {nextMilestone && (
            <div className={`hm-veh-obd-card hm-veh-milestone-card is-${nextMilestone.status}`} key={nextMilestone.miles}>
              <div className="hm-veh-obd-card-head">
                <strong>{nextMilestone.label}</strong>
                <span className={`hm-veh-priority ${milestoneStatus[nextMilestone.status]?.cls || "hm-veh-priority--unknown"}`}>{milestoneStatus[nextMilestone.status]?.label || nextMilestone.status}</span>
              </div>
              <p>{nextMilestone.sublabel}</p>
              <button
                type="button"
                className={`hm-veh-milestone-log-btn is-${nextMilestone.status}`}
                onClick={() => openMilestoneSheet(nextMilestone)}
              >
                Add last service →
              </button>
            </div>
          )}
        </div>
      )}

      {schedule.length > 0 && (
        <div className="hm-veh-obd-results" style={{ marginBottom: 12 }}>
          <div className="hm-veh-obd-results-label hm-veh-maint-section-title">
            <span>Maintenance schedule</span>
            <button type="button" onClick={() => onLogService?.()}>Update history →</button>
          </div>
          {allNoHistory && (
            <div className="hm-veh-maint-tip-card">
              <strong>Add service history</strong>
              <p>HouseMind will track what's due and what's coming up once your most recent services are saved.</p>
              <button type="button" onClick={() => onLogService?.()}>Update history →</button>
            </div>
          )}
          <div className="hm-veh-maint-compact-list">
            {(visibleSchedule.length ? visibleSchedule : schedule.slice(0, 1)).map(item => {
              const expanded = expandedScheduleType === item.service_type;
              const rowDueText = item.due_at_miles ? `Due at ${Number(item.due_at_miles).toLocaleString()} mi` : item.sublabel || item.reason;
              return (
                <button type="button" className={`hm-veh-maint-row${expanded ? " is-expanded" : ""}`} key={item.service_type} onClick={() => setExpandedScheduleType(expanded ? "" : item.service_type)}>
                  <span className="hm-veh-maint-row-main">
                    <strong>{item.title}</strong>
                    <em>{item.status === "no_history" ? "No history — add last service" : rowDueText}</em>
                    <span className={`hm-veh-priority ${scheduleStatus[item.status]?.cls || "hm-veh-priority--unknown"}`}>{scheduleStatus[item.status]?.label || item.status}</span>
                  </span>
                  {expanded && (
                    <span className="hm-veh-maint-row-detail">
                      <small>{item.status === "no_history" ? item.sublabel || item.reason : rowDueText}</small>
                      {item.source === "milestone" && item.source_label && <small className="hm-veh-maint-source">Last logged via {item.source_label}</small>}
                      <span className="hm-veh-maint-log-link" onClick={(event) => { event.stopPropagation(); onLogService?.(item.service_type); }}>Update history →</span>
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {hasAttentionSchedule && !showOkSchedule && hiddenOkCount > 0 && (
            <div className="hm-veh-maint-ok-toggle">
              <span>+ {hiddenOkCount} item{hiddenOkCount !== 1 ? "s" : ""} OK</span>
              <button type="button" onClick={() => setShowOkSchedule(true)}>Show all ↓</button>
            </div>
          )}
          {(!hasAttentionSchedule || showOkSchedule) && hiddenOkCount > 0 && (
            <div className="hm-veh-maint-ok-toggle">
              <span>{!hasAttentionSchedule ? "All items OK" : "Showing OK items"}</span>
              <button type="button" onClick={() => setShowOkSchedule(false)}>Hide OK ↑</button>
            </div>
          )}
        </div>
      )}

      {!result && (
        <button type="button" className="hm-veh-check-btn" onClick={generate} disabled={busy}>
          {busy ? "Analyzing your vehicle…" : "Generate maintenance plan"}
        </button>
      )}

      {result && (
        <div className="hm-maint-result">
          <div className="hm-veh-obd-results-label">After plan generated</div>
          {result.summary && <p className="hm-maint-summary">{result.summary}</p>}
          <Section items={result.overdue}   urgency="high"   />
          <Section items={result.due_soon}  urgency="medium" />
          <Section items={result.upcoming}  urgency="low"    />
          {!result.overdue?.length && !result.due_soon?.length && !result.upcoming?.length && (
            <div className="hm-vault-empty">No specific items identified. Add more service history for better advice.</div>
          )}
          <button type="button" className="hm-veh-refresh-btn" onClick={generate} disabled={busy}>
            {busy ? "Refreshing…" : "Refresh advice"}
          </button>
        </div>
      )}

      <VehicleServiceLinks hasDIY={result?.overdue?.some(i => i.diy_possible) || result?.due_soon?.some(i => i.diy_possible)} />
    </div>
  );
}

function ObdLookupTab({ vehicle }) {
  const [input, setInput] = useState("");
  const [results, setResults] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let ignore = false;
    vehiclesApi.listDiagnostics(vehicle.id)
      .then(data => { if (!ignore) setSessions(data || []); })
      .catch(() => { if (!ignore) setSessions([]); });
    return () => { ignore = true; };
  }, [vehicle.id]);

  async function lookup(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = await vehiclesApi.decodeObdCodes(input);
      setResults(data.codes || []);
    } catch (err) {
      setError(err.message || "Could not decode OBD-II codes");
    } finally {
      setBusy(false);
    }
  }

  async function saveSession() {
    if (!results.length) return;
    setSaveBusy(true);
    setError("");
    try {
      const created = await vehiclesApi.addDiagnostic(vehicle.id, {
        codes: results.map(item => item.code),
        decoded: { codes: results },
        mileage_at: vehicle.current_mileage || null,
      });
      setSessions(prev => [created, ...prev]);
    } catch (err) {
      setError(err.message || "Could not save diagnostic session");
    } finally {
      setSaveBusy(false);
    }
  }

  return (
    <div className="hm-veh-tab-content">
      <div className="hm-veh-tab-head"><h3>🔎 OBD-II lookup</h3></div>
      <form className="hm-veh-form hm-veh-obd-form" onSubmit={lookup}>
        <label className="hm-veh-form-span2">
          <span>Codes</span>
          <input
            className="hm-auth-input"
            placeholder="P0420 P0301 C0035"
            value={input}
            onChange={e => setInput(e.target.value.toUpperCase())}
          />
          <small className="hm-veh-field-help">Enter codes separated by spaces, e.g. P0420 P0301</small>
        </label>
        <div className="hm-veh-form-span2">
          <button type="submit" className="hm-veh-check-btn" disabled={busy || !input.trim()}>
            {busy ? "Looking up codes..." : "Decode codes"}
          </button>
        </div>
      </form>
      {error && <div className="hm-inline-error" style={{ marginTop: 10 }}>{error}</div>}
      {results.length === 0 ? (
        <div className="hm-veh-obd-help-card">
          <strong>What you can decode</strong>
          <ul>
            <li>Powertrain codes like P0420, P0301, or P0171.</li>
            <li>Body, chassis, and network codes when your scanner shows them.</li>
          </ul>
          <span>Use a Bluetooth OBD-II scanner or repair shop printout, then paste codes above.</span>
        </div>
      ) : (
        <div className="hm-veh-obd-results">
          <div className="hm-veh-obd-results-label">After decode</div>
          {results.map(item => (
            <div className="hm-veh-obd-card" key={item.code}>
              <div className="hm-veh-obd-card-head">
                <strong>{item.code}</strong>
                <span>{item.system}</span>
              </div>
              <h4>{item.title}</h4>
              <p>{item.description}</p>
              <div className={`hm-veh-priority hm-veh-priority--${item.urgency}`}>{item.urgency}</div>
              {!item.known && <small>Generic local lookup. Confirm manufacturer-specific codes with a scan tool or service manual.</small>}
            </div>
          ))}
          <button type="button" className="hm-veh-check-btn" onClick={saveSession} disabled={saveBusy}>
            {saveBusy ? "Saving..." : "Save diagnostic session"}
          </button>
        </div>
      )}
      {sessions.length > 0 && (
        <div className="hm-veh-obd-results" style={{ marginTop: 12 }}>
          <div className="hm-veh-obd-results-label">Diagnostic history</div>
          {sessions.slice(0, 3).map(session => (
            <div className="hm-veh-obd-card" key={session.id}>
              <div className="hm-veh-obd-card-head">
                <strong>{(session.codes || []).join(" ") || "Diagnostic scan"}</strong>
                <span>{session.resolved ? "Resolved" : "Open"}</span>
              </div>
              <p>{session.mileage_at ? `${Number(session.mileage_at).toLocaleString()} mi · ` : ""}{fmtDate((session.created_at || "").slice(0, 10))}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VehicleOverviewTab({ vehicle }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openComplaint, setOpenComplaint] = useState(null);
  const [recallsDrawerOpen, setRecallsDrawerOpen] = useState(false);
  const [complaintsDrawerOpen, setComplaintsDrawerOpen] = useState(false);
  const [selectedRecallIndex, setSelectedRecallIndex] = useState(null);
  const [recallsReviewing, setRecallsReviewing] = useState(false);
  const [recallsReviewError, setRecallsReviewError] = useState("");
  const closeRecallSheet = useCallback(() => {
    setSelectedRecallIndex(null);
  }, []);
  const recallSheetDrag = useSheetHandleDrag(closeRecallSheet, 54);

  const loadOverview = useCallback((ignoreRef) => {
    setLoading(true);
    return vehiclesApi.intelligence(vehicle.id)
      .then(result => { if (!ignoreRef?.current) setData(result); })
      .catch(err => { if (!ignoreRef?.current) setError(err.message || "Could not load vehicle overview"); })
      .finally(() => { if (!ignoreRef?.current) setLoading(false); });
  }, [vehicle.id]);

  useEffect(() => {
    const ignoreRef = { current: false };
    loadOverview(ignoreRef);
    return () => { ignoreRef.current = true; };
  }, [loadOverview]);

  async function markRecallsReviewed() {
    setRecallsReviewing(true);
    setRecallsReviewError("");
    try {
      await vehiclesApi.markRecallsReviewed(vehicle.id);
      setRecallsDrawerOpen(false);
      setSelectedRecallIndex(null);
      setData(current => ({
        ...(current || {}),
        recalls_resolved: true,
        recall_history: (current?.recalls || current?.recall_history || []),
        recalls: [],
      }));
      await loadOverview({ current: false });
    } catch (err) {
      setRecallsReviewError(err.message || "Could not mark recalls reviewed.");
    } finally {
      setRecallsReviewing(false);
    }
  }

  if (loading) return <div className="hm-vault-empty">Loading vehicle overview...</div>;
  if (error) return <div className="hm-inline-error">{error}</div>;

  const ownership = data?.ownership || {};
  const readiness = data?.trip_readiness || {};
  const recalls = data?.recalls || [];
  const recallHistory = data?.recall_history || [];
  const recallsResolved = !!data?.recalls_resolved;
  const complaints = data?.complaints || [];
  const complaintCount = data?.complaint_count ?? vehicle.complaint_count ?? complaints.length;
  const vaultRecords = data?.vault_records || [];
  const allRecallItems = recallsResolved && recallHistory.length > 0 ? recallHistory : recalls;
  const recallCountLabel = recallsResolved && recallHistory.length > 0
    ? `${recallHistory.length} completed recall${recallHistory.length !== 1 ? "s" : ""}`
    : `${recalls.length} open recall${recalls.length !== 1 ? "s" : ""}`;
  const selectedRecall = selectedRecallIndex !== null ? allRecallItems[selectedRecallIndex] : null;

  return (
    <div className="hm-veh-tab-content">
      {selectedRecall && (
        <div className="hm-gifts-sheet-overlay hm-veh-service-sheet-overlay" onClick={closeRecallSheet}>
      <BackButtonTrap onClose={closeRecallSheet} />
          <section
            ref={recallSheetDrag.sheetRef}
            className="gf-add-person-sheet hm-veh-service-sheet hm-veh-recall-sheet"
            onClick={event => event.stopPropagation()}
            aria-label="Recall details"
          >
            <div className="hm-gifts-sheet-handle" {...recallSheetDrag.handleProps} />
            <SheetLogoRow title="Recall details" onClose={closeRecallSheet} closeLabel="Close recall details" />
            <div className="hm-veh-service-sheet-body hm-veh-recall-sheet-body">
              <div className="hm-veh-recall-sheet-summary">
                <strong>{fmtRecallCategory(selectedRecall.component)}</strong>
                <span>{recallsResolved ? "Completed recall saved in history." : "Review NHTSA details and contact the manufacturer for next steps."}</span>
              </div>
              <div className="hm-veh-recall-sheet-card">
                <strong>Summary</strong>
                <p>{selectedRecall.summary || "Review this recall with the manufacturer."}</p>
              </div>
              <div className="hm-veh-recall-detail hm-veh-recall-detail--sheet">
                {selectedRecall.consequence && <small><b>Risk:</b> {selectedRecall.consequence}</small>}
                {selectedRecall.remedy && <small><b>Remedy:</b> {selectedRecall.remedy}</small>}
                {selectedRecall.campaign_number && <small><b>NHTSA:</b> {selectedRecall.campaign_number}</small>}
                {selectedRecall.report_received_date && <small><b>Reported:</b> {selectedRecall.report_received_date}</small>}
              </div>
            </div>
          </section>
        </div>
      )}
      <div className="hm-veh-tab-head"><h3>🚗 Overview</h3></div>
      <div className="hm-veh-overview-stat-grid">
        <div className="hm-veh-overview-stat">
          <strong>{vehicle.current_mileage ? Number(vehicle.current_mileage).toLocaleString() : "-"}</strong>
          <span>Current miles</span>
        </div>
        <div className="hm-veh-overview-stat">
          <strong>{vehicle.purchase_price ? fmt$(vehicle.purchase_price) : "-"}</strong>
          <span>Paid</span>
        </div>
        <div className="hm-veh-overview-stat">
          <strong>{yearsOwnedLabel(vehicle.purchase_date)}</strong>
          <span>Owned</span>
        </div>
        <div className="hm-veh-overview-stat">
          <strong>{[vehicle.color, vehicle.trim].filter(Boolean).join(" ") || "-"}</strong>
          <span>Trim / color</span>
        </div>
      </div>

      <div className="hm-veh-premium-grid hm-veh-overview-grid">
        <div className="hm-veh-premium-card hm-veh-overview-recalls">
          <div className="hm-veh-card-title-row">
            <strong>{recallsResolved ? "Recall history" : "NHTSA recalls"}</strong>
            {allRecallItems.length > 0 && (
              <button
                type="button"
                onClick={() => setRecallsDrawerOpen(open => !open)}
                aria-label={recallsDrawerOpen ? "Hide recalls" : "View recalls"}
              >
                {recallsDrawerOpen ? "^" : "v"}
              </button>
            )}
          </div>
          {allRecallItems.length > 0 ? (
            <div className="hm-veh-recall-drawer">
              {recallsReviewError && <div className="hm-inline-error">{recallsReviewError}</div>}
              <button
                type="button"
                className="hm-veh-recall-summary"
                onClick={() => setRecallsDrawerOpen(open => !open)}
                aria-expanded={recallsDrawerOpen}
              >
                <span>
                  <strong>{recallCountLabel}</strong>
                  <small>{recallsResolved ? "Marked done. Saved here as history." : "Tap to review NHTSA details."}</small>
                </span>
                <em aria-hidden="true">{recallsDrawerOpen ? "^" : "v"}</em>
              </button>
              {!recallsResolved && (
                <button
                  type="button"
                  className="hm-veh-recall-reviewed-btn"
                  onClick={markRecallsReviewed}
                  disabled={recallsReviewing}
                >
                  {recallsReviewing ? "Saving..." : "Mark reviewed"}
                </button>
              )}
              {recallsDrawerOpen && (
                <div className="hm-veh-recall-list hm-veh-recall-list--drawer">
                  {allRecallItems.map((recall, i) => (
                    <button
                      type="button"
                      key={recall.campaign_number || i}
                      className="hm-veh-recall-row"
                      onClick={() => setSelectedRecallIndex(i)}
                      aria-label={`Open recall details for ${fmtRecallCategory(recall.component)}`}
                    >
                      <span>{fmtRecallCategory(recall.component)}</span>
                      <small aria-hidden="true">&gt;</small>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p>No open recall details found from NHTSA for this vehicle.</p>
          )}
        </div>

        <div className="hm-veh-premium-card">
          <strong>Cost of ownership</strong>
          <div className="hm-veh-cost-total">{fmt$(ownership.month_total || 0)}<span> this month</span></div>
          <p>{fmt$(ownership.year_total || 0)} tracked this year across fuel, service, insurance, and registration.</p>
          <div className="hm-veh-mini-breakdown">
            <span>Fuel {fmt$(ownership.fuel_month || 0)}</span>
            <span>Service {fmt$(ownership.service_month || 0)}</span>
            <span>Insurance {fmt$(ownership.insurance_month || 0)}</span>
          </div>
        </div>

        <div className="hm-veh-premium-card">
          <strong>Trip readiness</strong>
          {readiness.trip ? (
            <>
              <div className="hm-veh-readiness-score">{readiness.score}%</div>
              <p>{readiness.trip.destination || readiness.trip.title || "Upcoming trip"} starts {fmtDate(readiness.trip.event_date)}.</p>
              {readiness.items?.length > 0 ? (
                <ul className="hm-veh-tight-list">{readiness.items.map((item, i) => <li key={i}>{item}</li>)}</ul>
              ) : (
                <p>No blocking vehicle issues found for this trip.</p>
              )}
            </>
          ) : (
            <p>No upcoming trips in the next 45 days.</p>
          )}
        </div>

        <div className="hm-veh-premium-card hm-veh-overview-complaints">
          <div className="hm-veh-card-title-row">
            <strong>NHTSA complaints</strong>
            {complaintCount > 0 && (
              <button
                type="button"
                onClick={() => {
                  setComplaintsDrawerOpen(open => !open);
                  setOpenComplaint(null);
                }}
                aria-label={complaintsDrawerOpen ? "Hide complaints" : "View complaints"}
              >
                {complaintsDrawerOpen ? "^" : "v"}
              </button>
            )}
          </div>
          {complaintCount > 0 ? (
            <div className="hm-veh-recall-drawer">
              <button
                type="button"
                className="hm-veh-recall-summary"
                onClick={() => {
                  setComplaintsDrawerOpen(open => !open);
                  setOpenComplaint(null);
                }}
                aria-expanded={complaintsDrawerOpen}
              >
                <span>
                  <strong>{complaintCount} owner complaint{complaintCount !== 1 ? "s" : ""} found</strong>
                  <small>Tap to review reported owner complaints.</small>
                </span>
                <em aria-hidden="true">{complaintsDrawerOpen ? "^" : "v"}</em>
              </button>
              {complaintsDrawerOpen && (
                <div className="hm-veh-recall-list hm-veh-recall-list--drawer">
                  {complaints.slice(0, 3).map((complaint, i) => (
                    <button
                      type="button"
                      key={complaint.odi_number || i}
                      className={`hm-veh-recall-row${openComplaint === i ? " is-open" : ""}`}
                      onClick={() => setOpenComplaint(openComplaint === i ? null : i)}
                    >
                      <span className="hm-veh-complaint-cat">{fmtComplaintCategory(complaint.components)}</span>
                      <p>{complaint.summary || "NHTSA complaint summary unavailable."}</p>
                      {openComplaint === i && (
                        <div className="hm-veh-recall-detail">
                          {complaint.date_filed && <small><b>Filed:</b> {complaint.date_filed}</small>}
                          {(complaint.crash || complaint.fire || complaint.injuries || complaint.deaths) && (
                            <small><b>Safety flags:</b> {[
                              complaint.crash ? "crash" : null,
                              complaint.fire ? "fire" : null,
                              complaint.injuries ? `${complaint.injuries} injuries` : null,
                              complaint.deaths ? `${complaint.deaths} deaths` : null,
                            ].filter(Boolean).join(", ")}</small>
                          )}
                          {complaint.odi_number && <small><b>ODI:</b> {complaint.odi_number}</small>}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p>No NHTSA owner complaints found for this year, make, and model.</p>
          )}
        </div>

        <div className="hm-veh-premium-card">
          <strong>Vehicle vault</strong>
          {vaultRecords.length > 0 ? (
            <div className="hm-veh-vault-list">
              {vaultRecords.slice(0, 4).map(record => (
                <a key={record.id} href="/app/home-vault" className="hm-veh-vault-link">
                  <span>{record.title}</span>
                  <small>{record.record_type}{record.due_date ? ` · due ${fmtDate(record.due_date)}` : ""}</small>
                </a>
              ))}
            </div>
          ) : (
            <div className="hm-veh-vault-empty">
              <span>No linked vault records yet.</span>
              <a href={`/app/home-vault?vehicle_id=${vehicle.id}&record_type=warranty&title=${encodeURIComponent(`${vehicle.display_name || "Vehicle"} warranty`)}`}>+ Add warranty →</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FuelTab({ vehicle, onMileageUpdate }) {
  const [logs, setLogs] = useState([]);
  const [fuelPrices, setFuelPrices] = useState(null);
  const [form, setForm] = useState(BLANK_FUEL);
  const [showForm, setShowForm] = useState(false);
  const [editingFuelId, setEditingFuelId] = useState(null);
  const [openFuelMenuId, setOpenFuelMenuId] = useState(null);
  const [confirmDeleteFuel, setConfirmDeleteFuel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [deleteBusyId, setDeleteBusyId] = useState(null);
  const [error, setError] = useState("");
  const showWorking = useMinLoad(busy || !!deleteBusyId, 700);
  const closeFuelSheet = useCallback(() => { setShowForm(false); setEditingFuelId(null); setForm(BLANK_FUEL); }, []);
  const fuelSheetDrag = useSheetHandleDrag(closeFuelSheet, 54);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      setLogs(await vehiclesApi.listFuel(vehicle.id) || []);
    } catch (err) {
      setError(err.message || "Could not load fuel logs");
    } finally {
      setLoading(false);
    }
  }, [vehicle.id]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  useEffect(() => {
    let ignore = false;
    vehiclesApi.fuelPrices(vehicle.state)
      .then(data => { if (!ignore) setFuelPrices(data); })
      .catch(() => { if (!ignore) setFuelPrices(null); });
    return () => { ignore = true; };
  }, [vehicle.state]);

  function setField(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleAdd(e) {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const payload = {
        ...form,
        odometer_reading: form.odometer_reading ? parseInt(form.odometer_reading, 10) : null,
        gallons_added: form.gallons_added ? parseFloat(form.gallons_added) : null,
        price_per_gallon: form.price_per_gallon ? parseFloat(form.price_per_gallon) : null,
        total_cost: form.total_cost ? parseFloat(form.total_cost) : autoFuelTotal != null ? Number(autoFuelTotal.toFixed(2)) : null,
      };
      const saved = editingFuelId
        ? await vehiclesApi.updateFuel(vehicle.id, editingFuelId, payload)
        : await vehiclesApi.addFuel(vehicle.id, payload);
      setLogs(prev => editingFuelId ? prev.map(log => log.id === editingFuelId ? saved : log) : [saved, ...prev]);
      if (payload.odometer_reading && (!vehicle.current_mileage || payload.odometer_reading > vehicle.current_mileage)) {
        onMileageUpdate(vehicle.id, payload.odometer_reading);
      }
      setForm(BLANK_FUEL);
      setEditingFuelId(null);
      setShowForm(false);
    } catch (err) {
      setError(err.message || "Could not save fuel log");
    } finally {
      setBusy(false);
    }
  }

  function startFillUpEdit(log) {
    setOpenFuelMenuId(null);
    setEditingFuelId(log.id);
    setForm({
      fill_date: log.fill_date || "",
      odometer_reading: log.odometer_reading != null ? String(log.odometer_reading) : "",
      gallons_added: log.gallons_added != null ? String(log.gallons_added) : "",
      price_per_gallon: log.price_per_gallon != null ? String(log.price_per_gallon) : "",
      total_cost: log.total_cost != null ? String(log.total_cost) : "",
      station_name: log.station_name || "",
      station_location: log.station_location || "",
      is_full_fill: log.is_full_fill !== false,
      notes: log.notes || "",
    });
    setShowForm(true);
  }

  async function deleteLog(id) {
    setDeleteBusyId(id);
    setError("");
    try {
      await vehiclesApi.deleteFuel(vehicle.id, id);
      setLogs(prev => prev.filter(log => log.id !== id));
    } catch (err) {
      setError(err.message || "Could not delete fuel log");
    } finally {
      setDeleteBusyId(null);
    }
  }

  const sorted = [...logs].sort((a, b) => (a.odometer_reading || 0) - (b.odometer_reading || 0));
  const mpgSamples = sorted.slice(1).map((log, index) => {
    const previous = sorted[index];
    if (!log.is_full_fill || !previous?.odometer_reading || !log.odometer_reading || !log.gallons_added) return null;
    const miles = log.odometer_reading - previous.odometer_reading;
    return miles > 0 ? miles / log.gallons_added : null;
  }).filter(Boolean);
  const averageMpg = mpgSamples.length ? mpgSamples.reduce((sum, value) => sum + value, 0) / mpgSamples.length : null;
  const totalFuelCost = logs.reduce((sum, log) => sum + (Number(log.total_cost) || 0), 0);
  const autoFuelTotal = form.gallons_added && form.price_per_gallon
    ? Number(form.gallons_added) * Number(form.price_per_gallon)
    : null;
  const openFuelMenuLog = openFuelMenuId ? logs.find(log => log.id === openFuelMenuId) : null;

  return (
    <div className="hm-veh-tab-content">
      {showWorking && <SavingNotice title={deleteBusyId ? "Removing" : "Saving"} message={deleteBusyId ? "Removing fuel log..." : "Saving fuel log..."} />}
      {confirmDeleteFuel && (
        <ConfirmModal
          tag="⛽ Delete fill-up"
          title="Delete fill-up?"
          message="This fill-up will be removed from your fuel history."
          confirmLabel="Delete fill-up"
          onCancel={() => setConfirmDeleteFuel(null)}
          onConfirm={async () => { const target = confirmDeleteFuel; setConfirmDeleteFuel(null); await deleteLog(target.id); }}
          loading={deleteBusyId === confirmDeleteFuel.id}
        />
      )}
      {openFuelMenuLog && (
        <div className="hm-veh-action-modal-overlay" onClick={() => setOpenFuelMenuId(null)}>
          <section className="hm-signout-modal hm-veh-action-modal" onClick={event => event.stopPropagation()} aria-label="Fill-up actions">
            <div className="hm-veh-action-modal-head">
              <div>
                <strong>Fill-up actions</strong>
                <span>{openFuelMenuLog.fill_date ? fmtDate(openFuelMenuLog.fill_date) : "Fuel fill"}</span>
              </div>
              <button type="button" onClick={() => setOpenFuelMenuId(null)} aria-label="Close fill-up actions">×</button>
            </div>
            <div className="hm-veh-action-modal-body">
              <button type="button" onClick={() => startFillUpEdit(openFuelMenuLog)}>Edit fill-up</button>
              <button type="button" className="is-danger" onClick={() => { setOpenFuelMenuId(null); setConfirmDeleteFuel(openFuelMenuLog); }}>Delete fill-up</button>
            </div>
          </section>
        </div>
      )}
      <div className="hm-veh-tab-head hm-veh-fuel-head">
        <h3>⛽ Fuel & MPG</h3>
        {!showForm && (
          <div className="hm-veh-tab-actions hm-veh-fuel-actions">
            <button type="button" className="hm-veh-add-service-btn" onClick={() => { setEditingFuelId(null); setForm(BLANK_FUEL); setShowForm(true); }}>
              <span>+</span> Fill-up
            </button>
          </div>
        )}
      </div>
      {error && <div className="hm-inline-error" style={{ marginBottom: 10 }}>{error}</div>}
      <div className="hm-veh-facts hm-veh-fuel-facts">
        <div className="hm-veh-fact"><span>Average MPG</span><strong>{averageMpg ? averageMpg.toFixed(1) : "After 2 full fills"}</strong></div>
        <div className="hm-veh-fact"><span>Fuel spend</span><strong>{fmtMoney(totalFuelCost)}</strong></div>
        <div className="hm-veh-fact"><span>Fill-ups</span><strong>{logs.length}</strong></div>
      </div>
      {fuelPrices && (
        <div className="hm-veh-scan-mini-panel" style={{ marginBottom: 12 }}>
          <div>
            <strong>{fuelPrices.selected ? `${fmtMoney(fuelPrices.selected.price)} regional regular` : "Regional fuel prices"}</strong>
            <span>{fuelPrices.selected ? `${fuelPrices.region} weekly EIA average · ${fuelPrices.selected.period || ""}` : fuelPrices.message || "EIA fuel price feed not available."}</span>
          </div>
          <a href={fuelPrices.data_url} target="_blank" rel="noreferrer">EIA →</a>
        </div>
      )}
      {showForm && (
        <div className="hm-gifts-sheet-overlay hm-veh-service-sheet-overlay" onClick={closeFuelSheet}>
      <BackButtonTrap onClose={closeFuelSheet} />
          <section ref={fuelSheetDrag.sheetRef} className="gf-add-person-sheet hm-veh-service-sheet" onClick={event => event.stopPropagation()} aria-label="Log fill-up">
            <div className="hm-gifts-sheet-handle" {...fuelSheetDrag.handleProps} />
            <SheetLogoRow title="Log fill-up" onClose={closeFuelSheet} closeLabel="Close fill-up sheet" />
            <form onSubmit={handleAdd} className="hm-veh-service-sheet-body hm-veh-service-sheet-form">
              <div className="hm-veh-sheet-grid hm-veh-sheet-grid--2">
                <label className="hm-veh-sheet-field"><span>Date</span><input className="hm-auth-input" type="date" value={form.fill_date} onChange={e => setField("fill_date", e.target.value)} /></label>
                <label className="hm-veh-sheet-field"><span>Odometer</span><input className="hm-auth-input" type="number" min="0" value={form.odometer_reading} onChange={e => setField("odometer_reading", e.target.value)} /></label>
                <label className="hm-veh-sheet-field"><span>Gallons</span><input className="hm-auth-input" type="number" min="0" step="0.001" value={form.gallons_added} onChange={e => setField("gallons_added", e.target.value)} /></label>
                <label className="hm-veh-sheet-field"><span>Price per gallon</span><input className="hm-auth-input" type="number" min="0" step="0.001" value={form.price_per_gallon} onChange={e => setField("price_per_gallon", e.target.value)} /></label>
              </div>
              <div className="hm-veh-fuel-total-preview">Estimated total: <strong>{form.total_cost ? fmtMoney(form.total_cost) : autoFuelTotal != null ? fmtMoney(autoFuelTotal) : "$0.00"}</strong></div>
              <label className="hm-veh-sheet-field"><span>Total cost</span><input className="hm-auth-input" type="number" min="0" step="0.01" value={form.total_cost} onChange={e => setField("total_cost", e.target.value)} /></label>
              <label className="hm-veh-sheet-field"><span>Station optional</span><input className="hm-auth-input" value={form.station_name} onChange={e => setField("station_name", e.target.value)} /></label>
              <label className="hm-veh-checkline"><input type="checkbox" checked={form.is_full_fill} onChange={e => setField("is_full_fill", e.target.checked)} /> Full tank fill-up</label>
              <div className="hm-veh-sheet-footer hm-veh-sheet-footer--inline">
                <button type="button" className="hm-veh-sheet-link" onClick={closeFuelSheet}>Cancel</button>
                <button type="submit" className="hm-veh-sheet-primary" disabled={busy}>{busy ? "Saving..." : "Save fill-up"}</button>
              </div>
            </form>
          </section>
        </div>
      )}
      {loading ? <div className="hm-vault-empty">Loading...</div> : logs.length === 0 ? (
        <div className="hm-veh-tab-empty">
          <div className="hm-veh-tab-empty-icon">⛽</div>
          <strong>No fill-ups logged yet</strong>
          <p>Log your first fill-up to start tracking fuel costs and MPG.</p>
        </div>
      ) : (
        <div className="hm-veh-log-list">
          {logs.map(log => (
            <article className="hm-veh-log-item" key={log.id}>
              <div className="hm-veh-log-type">{log.fill_date ? fmtDate(log.fill_date) : "Fuel fill"}</div>
              <div className="hm-veh-log-meta">
                {log.odometer_reading && <span>{fmtMiles(log.odometer_reading)}</span>}
                {log.gallons_added && <span>{log.gallons_added} gal</span>}
                {log.station_name && <span>{log.station_name}</span>}
                {log.total_cost != null && <span className="hm-veh-log-cost">{fmtMoney(log.total_cost)}</span>}
              </div>
              <div className="hm-veh-row-menu">
                <button type="button" onClick={() => setOpenFuelMenuId(openFuelMenuId === log.id ? null : log.id)} aria-label="Fill-up actions">···</button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function CoverageTab({ vehicle }) {
  const [insurance, setInsurance] = useState(BLANK_INSURANCE);
  const [registration, setRegistration] = useState(BLANK_REGISTRATION);
  const [claims, setClaims] = useState([]);
  const [claimDraft, setClaimDraft] = useState({ claim_date: "", claim_type: "insurance", description: "", amount_claimed: "", amount_paid: "", status: "open", claim_number: "", insurer: "" });
  const [showClaimSheet, setShowClaimSheet] = useState(false);
  const [editingCoverage, setEditingCoverage] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const showWorking = useMinLoad(busy, 700);
  const closeCoverageSheet = useCallback(() => setEditingCoverage(false), []);
  const coverageSheetDrag = useSheetHandleDrag(closeCoverageSheet, 54);
  const closeClaimSheet = useCallback(() => setShowClaimSheet(false), []);
  const claimSheetDrag = useSheetHandleDrag(closeClaimSheet, 54);
  const policyExpiryLabel = insurance.policy_expiry ? fmtDate(insurance.policy_expiry) : "";

  useEffect(() => {
    let ignore = false;
    Promise.all([vehiclesApi.getInsurance(vehicle.id), vehiclesApi.getRegistration(vehicle.id)])
      .then(([ins, reg]) => {
        if (ignore) return;
        setInsurance({ ...BLANK_INSURANCE, ...(ins || {}) });
        setRegistration({ ...BLANK_REGISTRATION, ...(reg || {}) });
      })
      .catch(err => setError(err.message || "Could not load coverage details"));
    return () => { ignore = true; };
  }, [vehicle.id]);

  useEffect(() => {
    let ignore = false;
    vehiclesApi.listClaims(vehicle.id)
      .then(data => { if (!ignore) setClaims(data || []); })
      .catch(() => { if (!ignore) setClaims([]); });
    return () => { ignore = true; };
  }, [vehicle.id]);

  async function saveClaim() {
    setBusy(true); setError(""); setSuccess("");
    try {
      const created = await vehiclesApi.addClaim(vehicle.id, claimDraft);
      setClaims(prev => [created, ...prev]);
      setClaimDraft({ claim_date: "", claim_type: "insurance", description: "", amount_claimed: "", amount_paid: "", status: "open", claim_number: "", insurer: "" });
      setShowClaimSheet(false);
      setSuccess("Claim saved.");
    } catch (err) {
      setError(err.message || "Could not save claim");
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    setBusy(true); setError(""); setSuccess("");
    try {
      const insPayload = {
        ...insurance,
        deductible: insurance.deductible ? parseFloat(insurance.deductible) : null,
        monthly_premium: insurance.monthly_premium ? parseFloat(insurance.monthly_premium) : null,
      };
      const regPayload = {
        ...registration,
        annual_fee: registration.annual_fee ? parseFloat(registration.annual_fee) : null,
      };
      const [savedIns, savedReg] = await Promise.all([
        vehiclesApi.saveInsurance(vehicle.id, insPayload),
        vehiclesApi.saveRegistration(vehicle.id, regPayload),
      ]);
      setInsurance({ ...BLANK_INSURANCE, ...(savedIns || {}) });
      setRegistration({ ...BLANK_REGISTRATION, ...(savedReg || {}) });
      setEditingCoverage(false);
      setSuccess("Coverage and registration saved.");
    } catch (err) {
      setError(err.message || "Could not save details");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="hm-veh-tab-content">
      {showWorking && <SavingNotice title="Saving" message="Saving insurance and registration..." />}
      <div className="hm-veh-tab-head"><h3>📄 Insurance & registration</h3></div>
      {error && <div className="hm-inline-error" style={{ marginBottom: 10 }}>{error}</div>}
      {success && <div className="hm-inline-success" style={{ marginBottom: 10 }}>{success}</div>}
      <div className="hm-veh-scan-mini-panel">
        <div>
          <strong>Scan vehicle paperwork</strong>
          <span>Use the Home Vault scan flow for insurance cards, registration, warranties, and manuals.</span>
        </div>
        <a href={`/app/home-vault?vehicle_id=${vehicle.id}&record_type=warranty&title=${encodeURIComponent(`${vehicle.display_name || "Vehicle"} paperwork`)}`}>Open vault scan →</a>
      </div>
      <div className="hm-veh-coverage-read">
        <div className="hm-veh-coverage-read-head">
          <h4>Insurance & registration</h4>
          <button type="button" onClick={() => setEditingCoverage(true)}>Edit →</button>
        </div>
        {[
          ["Provider", insurance.provider_name],
          ["Policy no.", insurance.policy_number],
          ["Coverage", insurance.coverage_type],
          ["Deductible", insurance.deductible ? fmtMoney(insurance.deductible) : ""],
          ["Premium", insurance.monthly_premium ? `${fmtMoney(insurance.monthly_premium)} / mo` : ""],
        ].map(([label, value]) => (
          <div className="hm-veh-coverage-row" key={label}>
            <span>{label}</span>
            <strong>{value || "Not set"}</strong>
          </div>
        ))}
        <div className="hm-veh-coverage-row">
          <span>Expires</span>
          <strong>{policyExpiryLabel ? <em className="hm-veh-expiry-badge">{policyExpiryLabel}</em> : "Not set"}</strong>
        </div>
      </div>
      <div className="hm-veh-coverage-read" style={{ marginTop: 12 }}>
        <div className="hm-veh-coverage-read-head">
          <h4>Claims history</h4>
          <button type="button" onClick={() => setShowClaimSheet(true)}>Add claim →</button>
        </div>
        {claims.length ? claims.slice(0, 3).map(claim => (
          <div className="hm-veh-coverage-row" key={claim.id}>
            <span>{claim.claim_date ? fmtDate(claim.claim_date) : claim.claim_type}</span>
            <strong>{claim.amount_claimed ? fmtMoney(claim.amount_claimed) : claim.status || "Open"}</strong>
          </div>
        )) : (
          <div className="hm-veh-claims-empty">
            <span>No claims logged for this vehicle yet.</span>
          </div>
        )}
      </div>
      {editingCoverage && (
        <div className="hm-gifts-sheet-overlay hm-veh-service-sheet-overlay hm-veh-coverage-sheet-overlay" onClick={closeCoverageSheet}>
          <section ref={coverageSheetDrag.sheetRef} className="gf-add-person-sheet hm-veh-service-sheet hm-veh-coverage-sheet" onClick={event => event.stopPropagation()} aria-label="Edit insurance">
            <div className="hm-gifts-sheet-handle" {...coverageSheetDrag.handleProps} />
            <SheetLogoRow title="Edit insurance" onClose={closeCoverageSheet} closeLabel="Close edit insurance" />
            <div className="hm-veh-service-sheet-body hm-veh-service-sheet-form">
              <div className="hm-veh-sheet-grid hm-veh-sheet-grid--2">
                <label className="hm-veh-sheet-field"><span>Insurance provider</span><input className="hm-auth-input" value={insurance.provider_name || ""} onChange={e => setInsurance(v => ({ ...v, provider_name: e.target.value }))} /></label>
                <label className="hm-veh-sheet-field"><span>Policy number</span><input className="hm-auth-input" value={insurance.policy_number || ""} onChange={e => setInsurance(v => ({ ...v, policy_number: e.target.value }))} /></label>
                <label className="hm-veh-sheet-field"><span>Coverage type</span><input className="hm-auth-input" placeholder="Full, liability..." value={insurance.coverage_type || ""} onChange={e => setInsurance(v => ({ ...v, coverage_type: e.target.value }))} /></label>
                <label className="hm-veh-sheet-field"><span>Deductible</span><input className="hm-auth-input" type="number" min="0" value={insurance.deductible || ""} onChange={e => setInsurance(v => ({ ...v, deductible: e.target.value }))} /></label>
                <label className="hm-veh-sheet-field"><span>Monthly premium</span><input className="hm-auth-input" type="number" min="0" step="0.01" value={insurance.monthly_premium || ""} onChange={e => setInsurance(v => ({ ...v, monthly_premium: e.target.value }))} /></label>
                <label className="hm-veh-sheet-field"><span>Policy expiry</span><input className="hm-auth-input" type="date" value={insurance.policy_expiry || ""} onChange={e => setInsurance(v => ({ ...v, policy_expiry: e.target.value }))} /></label>
              </div>
              <label className="hm-veh-checkline"><input type="checkbox" checked={!!insurance.roadside_assistance} onChange={e => setInsurance(v => ({ ...v, roadside_assistance: e.target.checked }))} /> Roadside included</label>
              <div className="hm-veh-sheet-footer hm-veh-sheet-footer--inline">
                <button type="button" className="hm-veh-sheet-link" onClick={closeCoverageSheet}>Cancel</button>
                <button type="button" className="hm-veh-sheet-primary" onClick={save} disabled={busy}>{busy ? "Saving..." : "Save"}</button>
              </div>
            </div>
          </section>
        </div>
      )}
      {showClaimSheet && (
        <div className="hm-gifts-sheet-overlay hm-veh-service-sheet-overlay" onClick={closeClaimSheet}>
      <BackButtonTrap onClose={closeClaimSheet} />
          <section ref={claimSheetDrag.sheetRef} className="gf-add-person-sheet hm-veh-service-sheet hm-veh-coverage-sheet" onClick={event => event.stopPropagation()} aria-label="Add claim">
            <div className="hm-gifts-sheet-handle" {...claimSheetDrag.handleProps} />
            <SheetLogoRow title="Add claim" onClose={closeClaimSheet} closeLabel="Close add claim" />
            <div className="hm-veh-service-sheet-body hm-veh-service-sheet-form">
              <div className="hm-veh-sheet-grid hm-veh-sheet-grid--2">
                <label className="hm-veh-sheet-field"><span>Claim date</span><input className="hm-auth-input" type="date" value={claimDraft.claim_date} onChange={e => setClaimDraft(v => ({ ...v, claim_date: e.target.value }))} /></label>
                <label className="hm-veh-sheet-field"><span>Status</span><select className="hm-auth-input" value={claimDraft.status} onChange={e => setClaimDraft(v => ({ ...v, status: e.target.value }))}><option value="open">Open</option><option value="paid">Paid</option><option value="denied">Denied</option><option value="closed">Closed</option></select></label>
                <label className="hm-veh-sheet-field"><span>Claim number</span><input className="hm-auth-input" value={claimDraft.claim_number} onChange={e => setClaimDraft(v => ({ ...v, claim_number: e.target.value }))} /></label>
                <label className="hm-veh-sheet-field"><span>Amount claimed</span><input className="hm-auth-input" type="number" min="0" step="0.01" value={claimDraft.amount_claimed} onChange={e => setClaimDraft(v => ({ ...v, amount_claimed: e.target.value }))} /></label>
              </div>
              <label className="hm-veh-sheet-field"><span>Description</span><textarea className="hm-auth-input" rows={3} value={claimDraft.description} onChange={e => setClaimDraft(v => ({ ...v, description: e.target.value }))} /></label>
              <div className="hm-veh-sheet-footer hm-veh-sheet-footer--inline">
                <button type="button" className="hm-veh-sheet-link" onClick={closeClaimSheet}>Cancel</button>
                <button type="button" className="hm-veh-sheet-primary" onClick={saveClaim} disabled={busy}>{busy ? "Saving..." : "Save claim"}</button>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function ExportTab({ vehicle }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [format, setFormat] = useState("csv");
  const showWorking = useMinLoad(busy, 700);

  async function exportCsvFile() {
    const logs = await vehiclesApi.listService(vehicle.id);
    const vehicleRows = [
      ["Vehicle"],
      ["Name", vehicle.display_name || ""],
      ["VIN", vehicle.vin || ""],
      ["Year", vehicle.year || ""],
      ["Make", vehicle.make || ""],
      ["Model", vehicle.model || ""],
      ["Trim", vehicle.trim || ""],
      ["Color", vehicle.color || ""],
      ["License plate", vehicle.license_plate || ""],
      ["State", vehicle.state || ""],
      ["Current mileage", vehicle.current_mileage || ""],
      ["Purchase date", vehicle.purchase_date || ""],
      ["Purchase price", vehicle.purchase_price ?? ""],
      ["Open recalls", vehicle.recall_count || 0],
      [""],
      ["Service history"],
    ];
    const rows = [...vehicleRows, ["Service type", "Date", "Mileage", "Shop", "Cost", "Next mileage", "Next date", "Notes"]];
    (logs || []).forEach(log => rows.push([
      SERVICE_TYPE_LABELS[log.service_type] || log.service_type || "",
      log.service_date || "",
      log.mileage_at_service || "",
      log.shop_name || "",
      log.cost ?? "",
      log.next_service_mileage || "",
      log.next_service_date || "",
      log.notes || "",
    ]));
    const csv = rows.map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(",")).join("\n");
    downloadTextFile(`${vehicle.display_name || "vehicle"} service history.csv`, csv, "text/csv");
  }

  async function exportRecord() {
    setBusy(true); setError("");
    setSuccess("");
    try {
      if (format === "pdf") {
        await vehiclesApi.exportPdf(vehicle.id);
      } else if (format === "email") {
        await vehiclesApi.emailExport(vehicle.id);
        setSuccess("Vehicle PDF emailed to your account.");
      } else {
        await exportCsvFile();
      }
    } catch (err) {
      setError(err.message || "Could not export vehicle record");
    } finally {
      setBusy(false);
    }
  }

  const exportLabel = format === "email" ? "Email vehicle record" : format === "pdf" ? "↓ Export PDF" : "↓ Export CSV";

  return (
    <div className="hm-veh-tab-content">
      {showWorking && <SavingNotice title="Exporting" message="Preparing vehicle record..." />}
      <div className="hm-veh-tab-head"><h3>📤 Vehicle export</h3></div>
      {error && <div className="hm-inline-error" style={{ marginBottom: 10 }}>{error}</div>}
      {success && <div className="hm-inline-success" style={{ marginBottom: 10 }}>{success}</div>}
      <div className="hm-veh-premium-grid">
        <div className="hm-veh-premium-card hm-veh-export-card">
          <strong>Garage record</strong>
          <p>A complete snapshot of this vehicle for your records, insurance claims, or resale.</p>
          <ul className="hm-veh-export-list">
            <li>VIN, make, model, year, trim, color</li>
            <li>Purchase price, date, mileage</li>
            <li>Service log history</li>
            <li>Active recalls</li>
          </ul>
          <div className="hm-veh-export-formats" role="radiogroup" aria-label="Export formats">
            {[
              ["pdf", "PDF"],
              ["csv", "CSV"],
              ["email", "Email"],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={format === value ? "is-active" : ""}
                role="radio"
                aria-checked={format === value}
                onClick={() => { setFormat(value); setError(""); setSuccess(""); }}
              >
                {label}
              </button>
            ))}
          </div>
          <button type="button" className="hm-veh-check-btn hm-veh-export-btn" onClick={exportRecord} disabled={busy}>{busy ? "Preparing..." : exportLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ── Vehicle Detail View ───────────────────────────────────────────────────────

function VehicleDetail({ vehicle: initialVehicle, activeTab, onTabChange, onBack, onUpdate, onDelete }) {
  const [vehicle, setVehicle] = useState(initialVehicle);
  const [editing, setEditing] = useState(false);
  const [editStep, setEditStep] = useState(1);
  const [draft, setDraft]     = useState({});
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState("");
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [showVehicleMenu, setShowVehicleMenu] = useState(false);
  const [servicePrefillType, setServicePrefillType] = useState("");
  const [returnToMaintenanceAfterService, setReturnToMaintenanceAfterService] = useState(false);
  const showEditSaving = useMinLoad(editBusy, 700);
  const closeEdit = useCallback(() => { setEditing(false); setEditStep(1); }, []);
  const editSheetDrag = useSheetHandleDrag(closeEdit);

  useEffect(() => { setVehicle(initialVehicle); }, [initialVehicle]);

  const setTab = useCallback((tab) => {
    onTabChange?.(tab);
  }, [onTabChange]);

  function startEdit() {
    setDraft({
      nickname:        vehicle.nickname       || "",
      vehicle_type:    vehicle.vehicle_type   || "car",
      year:            vehicle.year           ? String(vehicle.year) : "",
      make:            vehicle.make           || "",
      model:           vehicle.model          || "",
      trim:            vehicle.trim           || "",
      vin:             vehicle.vin            || "",
      color:           vehicle.color          || "",
      license_plate:   vehicle.license_plate  || "",
      state:           vehicle.state          || "",
      current_mileage: vehicle.current_mileage ? String(vehicle.current_mileage) : "",
      purchase_price:  vehicle.purchase_price  ? String(vehicle.purchase_price)  : "",
      purchase_date:   vehicle.purchase_date   || "",
      notes:           vehicle.notes          || "",
      photo_url:       vehicle.photo_url      || "",
    });
    setEditStep(1);
    setEditing(true);
  }

  function setDraftField(k, v) { setDraft(d => ({ ...d, [k]: v })); }

  async function saveEdit() {
    setEditBusy(true); setEditError("");
    try {
      const payload = {
        ...draft,
        make:            draft.make  === "__other__"  ? "" : draft.make,
        model:           draft.model === "__other__"  ? "" : draft.model,
        trim:            draft.trim  === "__manual__" ? "" : draft.trim,
        year:            draft.year            ? parseInt(draft.year, 10)            : null,
        current_mileage: draft.current_mileage ? parseInt(draft.current_mileage, 10) : null,
        purchase_price:  draft.purchase_price  ? parseFloat(draft.purchase_price)    : null,
      };
      const updated = await vehiclesApi.update(vehicle.id, payload);
      setVehicle(updated);
      onUpdate(updated);
      closeEdit();
    } catch (err) {
      setEditError(err.message || "Could not update vehicle");
    } finally {
      setEditBusy(false);
    }
  }

  function handleMileageUpdate(vehicleId, miles) {
    const updated = { ...vehicle, current_mileage: miles };
    setVehicle(updated);
    onUpdate(updated);
  }

  async function confirmDelete() {
    setDeleteBusy(true);
    setDeleteError("");
    try {
      await onDelete(vehicle.id);
    } catch (err) {
      setDeleteError(err.message || "Could not remove vehicle");
      setDeleteBusy(false);
    }
  }

  const openServiceLogWithType = useCallback((serviceType) => {
    setServicePrefillType(serviceType || MANUAL_SERVICE_ENTRY);
    setReturnToMaintenanceAfterService(true);
    setTab("service");
  }, [setTab]);

  const handleMaintenanceServiceSaved = useCallback(() => {
    if (!returnToMaintenanceAfterService) return;
    setReturnToMaintenanceAfterService(false);
    setTab("maintenance");
  }, [returnToMaintenanceAfterService, setTab]);

  const recallBadge = vehicle.recall_count > 0
    ? (
      <button type="button" className="hm-veh-recall-badge" onClick={() => setTab("overview")}>
        ⚠ {vehicle.recall_count} open recall{vehicle.recall_count !== 1 ? "s" : ""}
      </button>
    )
    : null;
  const draftTrimOpts = getTrims(draft.make, draft.model);

  return (
    <div className="hm-veh-detail">
      {showEditSaving && <SavingNotice title="Saving" message="Saving vehicle changes..." />}
      {deleteBusy && <SavingNotice title="Removing" message="Removing vehicle..." />}
      {confirmRemove && (
        <ConfirmVehicleRemoveModal
          vehicleName={vehicle.display_name}
          busy={deleteBusy}
          error={deleteError}
          onCancel={() => { if (!deleteBusy) { setConfirmRemove(false); setDeleteError(""); } }}
          onConfirm={confirmDelete}
        />
      )}
      <div className="hm-veh-identity-block">
        {/* Header */}
        <div className="hm-veh-detail-header">
          <button type="button" className="hm-veh-back-btn" onClick={onBack}>← Garage</button>
          <div className="hm-veh-detail-title">
            {isVehiclePhoto(vehicle.photo_url) ? (
              <img className="hm-veh-detail-photo" src={vehicle.photo_url} alt="" />
            ) : (
              <span className="hm-veh-detail-icon">{vehicleIcon(vehicle.vehicle_type)}</span>
            )}
            <div>
              <h1>{vehicle.display_name}</h1>
              <div className="hm-veh-detail-sub">
                {vehicle.is_private && <span>Private</span>}
                {vehicle.color && <span>{vehicle.color}</span>}
                {vehicle.license_plate && <span>{vehicle.license_plate}{vehicle.state ? ` · ${vehicle.state}` : ""}</span>}
                {vehicle.current_mileage && <span>{fmtMiles(vehicle.current_mileage)}</span>}
                {recallBadge}
              </div>
            </div>
          </div>
          <div className="hm-veh-detail-actions">
            <button type="button" onClick={startEdit} className="hm-veh-action-btn hm-veh-edit-action">✏️ Edit vehicle</button>
            <div className="hm-veh-more-wrap">
              <button
                type="button"
                className="hm-veh-more-btn"
                onClick={() => setShowVehicleMenu(v => !v)}
                aria-label="More vehicle actions"
                aria-expanded={showVehicleMenu}
              >
                ···
              </button>
              {showVehicleMenu && (
                <>
                  <button
                    type="button"
                    className="hm-veh-more-dismiss"
                    aria-label="Close vehicle actions"
                    onClick={() => setShowVehicleMenu(false)}
                  />
                  <div className="hm-veh-more-menu" role="menu" aria-label="Vehicle actions">
                    <div className="hm-veh-more-menu-head">
                      <button type="button" onClick={() => setShowVehicleMenu(false)} aria-label="Close vehicle actions">×</button>
                    </div>
                    <button type="button" role="menuitem" className="hm-veh-more-menu-edit" onClick={() => { setShowVehicleMenu(false); startEdit(); }}>
                      ✏️ Edit vehicle
                    </button>
                    <button type="button" role="menuitem" className="hm-veh-more-menu-danger" onClick={() => { setShowVehicleMenu(false); setConfirmRemove(true); }}>
                      🗑️ Remove vehicle
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Quick facts */}
        {!editing && (
          <div className="hm-veh-facts">
            <div className="hm-veh-fact"><span>VIN</span><strong>{vehicle.vin || "Not set"}</strong></div>
            <div className="hm-veh-fact"><span>Paid</span><strong>{vehicle.purchase_price ? fmt$(vehicle.purchase_price) : "-"}</strong></div>
            <div className="hm-veh-fact"><span>Purchased</span><strong>{vehicle.purchase_date ? fmtDate(vehicle.purchase_date) : "-"}</strong></div>
            <div className="hm-veh-fact"><span>Trim</span><strong>{vehicle.trim || "-"}</strong></div>
          </div>
        )}
      </div>

      {/* Edit form */}
      {editing && (
        <>
        <div className="hm-veh-edit-sheet-backdrop" onClick={closeEdit} />
        <div ref={editSheetDrag.sheetRef} className="hm-veh-edit-panel">
          <div className="hm-gifts-sheet-handle hm-veh-edit-sheet-handle" {...editSheetDrag.handleProps} />
          <SheetLogoRow title="Edit vehicle" onClose={closeEdit} closeLabel="Close edit vehicle" />
          <div className="gf-steps-row hm-veh-sheet-steps hm-veh-edit-steps">
            <span className={`gf-step-dot${editStep === 1 ? " gf-step-dot--active" : " gf-step-dot--done"}`} />
            <span className={`gf-step-dot${editStep === 2 ? " gf-step-dot--active" : ""}`} />
            <span className="gf-step-txt">Step {editStep} of 2 · {editStep === 1 ? "Identity" : "Details"}</span>
          </div>
          {editError && <div className="hm-inline-error" style={{ marginBottom: 10 }}>{editError}</div>}
          <div className="hm-veh-form hm-veh-edit-form-body">
            {editStep === 1 ? (
              <>
                <div className="hm-veh-form-span2">
                  <VehiclePhotoUpload photoUrl={draft.photo_url} onChange={url => setDraftField("photo_url", url)} />
                </div>
                <label>
                  <span>Nickname</span>
                  <input className="hm-auth-input" placeholder="Family SUV…"
                    value={draft.nickname} onChange={e => setDraftField("nickname", e.target.value)} />
                </label>
                <label>
                  <span>Vehicle type</span>
                  <select className="hm-auth-input" value={draft.vehicle_type}
                    onChange={e => setDraftField("vehicle_type", e.target.value)}>
                    {VEHICLE_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
                  </select>
                </label>
                <label>
                  <span>Make</span>
                  <select className="hm-auth-input" value={draft.make}
                    onChange={e => setDraft(d => ({ ...d, make: e.target.value, model: "", trim: "" }))}>
                    <option value="">Select make…</option>
                    {MAKE_LIST.map(m => <option key={m} value={m}>{m}</option>)}
                    <option value="__other__">Other / Not listed</option>
                  </select>
                </label>
                <label>
                  <span>Model</span>
                  {(draft.make && draft.make !== "__other__" && VEHICLE_MAKES[draft.make]) ? (
                    <select className="hm-auth-input" value={draft.model}
                      onChange={e => setDraft(d => ({ ...d, model: e.target.value, trim: "" }))}>
                      <option value="">Select model…</option>
                      {VEHICLE_MAKES[draft.make].map(m => <option key={m} value={m}>{m}</option>)}
                      <option value="__other__">Other / Not listed</option>
                    </select>
                  ) : (
                    <input className="hm-auth-input" placeholder="Model name…"
                      value={draft.model} onChange={e => setDraft(d => ({ ...d, model: e.target.value, trim: "" }))} />
                  )}
                </label>
                <label>
                  <span>Year</span>
                  <select className="hm-auth-input" value={draft.year}
                    onChange={e => setDraftField("year", e.target.value)}>
                    <option value="">Select year…</option>
                    {YEAR_LIST.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </label>
                <label>
                  <span>Trim</span>
                  {draftTrimOpts ? (
                    <select className="hm-auth-input" value={draft.trim}
                      onChange={e => setDraftField("trim", e.target.value)}>
                      <option value="">Select trim…</option>
                      {draftTrimOpts.map(t => <option key={t} value={t}>{t}</option>)}
                      <option value="__manual__">Other / Not listed</option>
                    </select>
                  ) : (
                    <input className="hm-auth-input" placeholder="XLE, Lariat, Sport…"
                      value={draft.trim} onChange={e => setDraftField("trim", e.target.value)} />
                  )}
                </label>
              </>
            ) : (
              <>
                <label>
                  <span>Color</span>
                  <select className="hm-auth-input" value={draft.color}
                    onChange={e => setDraftField("color", e.target.value)}>
                    <option value="">Select color…</option>
                    {CAR_COLORS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                <label>
                  <span>Current mileage</span>
                  <input className="hm-auth-input" type="number" placeholder="45000"
                    value={draft.current_mileage} onChange={e => setDraftField("current_mileage", e.target.value)} />
                </label>
                <label>
                  <span>Purchase price</span>
                  <input className="hm-auth-input" type="number" min="0" step="0.01" placeholder="39800"
                    value={draft.purchase_price} onChange={e => setDraftField("purchase_price", e.target.value)} />
                </label>
                <label>
                  <span>Purchase date</span>
                  <input className="hm-auth-input" type="date"
                    value={draft.purchase_date} onChange={e => setDraftField("purchase_date", e.target.value)} />
                </label>
                <label>
                  <span>License plate</span>
                  <input className="hm-auth-input" placeholder="ABC 1234"
                    value={draft.license_plate} onChange={e => setDraftField("license_plate", e.target.value)} />
                </label>
                <label>
                  <span>State</span>
                  <select className="hm-auth-input" value={draft.state}
                    onChange={e => setDraftField("state", e.target.value)}>
                    <option value="">Select state…</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <label className="hm-veh-form-span2">
                  <span>VIN</span>
                  <input className="hm-auth-input" maxLength={17} placeholder="17 characters"
                    value={draft.vin} onChange={e => setDraftField("vin", e.target.value.toUpperCase())} />
                </label>
                <label className="hm-veh-form-span2">
                  <span>Notes</span>
                  <textarea className="hm-auth-input" rows={2} value={draft.notes}
                    onChange={e => setDraftField("notes", e.target.value)} />
                </label>
              </>
            )}
          </div>
          <div className="hm-vault-form-actions hm-veh-edit-sheet-footer" style={{ marginTop: 12 }}>
            {editStep === 1 ? (
              <>
                <button type="button" className="hm-veh-edit-footer-primary" onClick={() => setEditStep(2)}>Next</button>
                <button type="button" className="hm-veh-edit-footer-link" onClick={closeEdit}>Cancel</button>
              </>
            ) : (
              <>
                <button type="button" className="hm-veh-edit-footer-primary" onClick={saveEdit} disabled={editBusy}>
                  {editBusy ? "Saving…" : "Save changes"}
                </button>
                <button type="button" className="hm-veh-edit-footer-link" onClick={() => setEditStep(1)}>Back</button>
              </>
            )}
          </div>
        </div>
        </>
      )}

      {/* Tabs */}
      <div className="hm-vault-tabs hm-veh-tabs-scroll" style={{ marginTop: 20 }} role="tablist" aria-label="Vehicle detail tabs">
        {[["overview","Overview"], ["service","Service log"], ["fuel","Fuel & MPG"], ["obd","OBD-II"], ["coverage","Insurance"], ["maintenance","Maintenance"], ["quote","Quote"], ["export","Export"]].map(([tab, label]) => (
          <button key={tab} type="button"
            className={`hm-vault-tab${activeTab === tab ? " is-active" : ""}`}
            role="tab"
            aria-selected={activeTab === tab}
            onClick={() => setTab(tab)}>
            {label}
          </button>
        ))}
      </div>

      {activeTab === "overview"     && <VehicleOverviewTab vehicle={vehicle} />}
      {activeTab === "service"      && <ServiceLogTab   vehicle={vehicle} onMileageUpdate={handleMileageUpdate} initialServiceType={servicePrefillType} onInitialServiceHandled={() => setServicePrefillType("")} onSavedFromMaintenance={handleMaintenanceServiceSaved} />}
      {activeTab === "fuel"         && <FuelTab         vehicle={vehicle} onMileageUpdate={handleMileageUpdate} />}
      {activeTab === "obd"          && <ObdLookupTab     vehicle={vehicle} />}
      {activeTab === "coverage"     && <CoverageTab     vehicle={vehicle} />}
      {activeTab === "maintenance"  && <MaintenanceTab  vehicle={vehicle} onLogService={openServiceLogWithType} onShowServiceHistory={() => setTab("service")} />}
      {activeTab === "quote"        && <QuoteCheckerTab vehicle={vehicle} />}
      {activeTab === "export"       && <ExportTab       vehicle={vehicle} />}
      {!editing && <VehiclesStickyAsk contextLabel={vehicle.display_name || "this vehicle"} vehicle={vehicle} />}
    </div>
  );
}

// ── Garage (main list view) ───────────────────────────────────────────────────

function GarageView({ vehicles, loading, onSelect, onAdd }) {
  if (loading) {
    return <div className="hm-vault-empty">Loading your garage…</div>;
  }
  function vehicleEdgeClass(vehicle) {
    if (vehicle.recall_count > 0) return "is-urgent";
    if (!vehicle.current_mileage) return "is-soon";
    return "is-calm";
  }

  const fleet = vehicleFleetSummary(vehicles);
  const needsMileage = vehicles.some(vehicle => !vehicle.current_mileage);
  const tip = needsMileage
    ? {
      icon: "💡",
      title: "Keep mileage updated",
      body: "Log mileage after each fill-up or service for accurate maintenance reminders.",
    }
    : fleet.totalRecalls > 0
      ? {
        icon: "⚠",
        title: "Review open recalls",
        body: "Open recall counts come from NHTSA. Check the vehicle overview before long trips.",
      }
      : {
        icon: "🧾",
        title: "Save vehicle paperwork",
        body: "Add warranties, manuals, registration, and insurance cards to Home Vault for easier tracking.",
      };

  return (
    <>
      {vehicles.length === 0 ? (
        <div className="hm-veh-empty-garage">
          <div className="hm-veh-empty-icon">🚗</div>
          <h2>Your garage is empty</h2>
          <p>Track service history, fuel costs, recalls, and maintenance for every vehicle you own.</p>
          <button type="button" className="hm-veh-add-first-btn" onClick={onAdd}>+ Add your first vehicle</button>
        </div>
      ) : (
        <>
          <div className="hm-veh-grid">
            {vehicles.map(v => (
              <button key={v.id} type="button" className={`hm-veh-card ${vehicleEdgeClass(v)}`} onClick={() => onSelect(v)}>
                <div className="hm-veh-card-icon">
                  {isVehiclePhoto(v.photo_url) ? <img src={v.photo_url} alt="" /> : vehicleIcon(v.vehicle_type)}
                </div>
                <div className="hm-veh-card-body">
                  <div className="hm-veh-card-name">{v.display_name}</div>
                  <div className="hm-veh-card-meta">
                    {v.color && <span>{v.color}</span>}
                    {v.current_mileage && <span>{fmtMiles(v.current_mileage)}</span>}
                    {v.license_plate && <span>{v.license_plate}</span>}
                  </div>
                  {v.recall_count > 0 && (
                    <div className="hm-veh-card-recall">⚠ {v.recall_count} open recall{v.recall_count !== 1 ? "s" : ""}</div>
                  )}
                </div>
                <div className="hm-veh-card-arrow">›</div>
              </button>
            ))}
          </div>

          <div className="hm-veh-fleet-summary">
            <div className="hm-veh-section-label">🚗 Your fleet</div>
            <div className="hm-veh-fleet-stats">
              <div><strong>{vehicles.length}</strong><span>🚗 Vehicles</span></div>
              <div><strong>{fleet.totalMiles ? `${Math.round(fleet.totalMiles / 1000)}k` : "0"}</strong><span>🛣️ Mi tracked</span></div>
              <div><strong>{fleet.totalRecalls}</strong><span>⚠️ Recalls</span></div>
            </div>
          </div>

          <div className="hm-veh-tip-card">
            <div className="hm-veh-tip-icon">{tip.icon}</div>
            <div>
              <strong>{tip.title}</strong>
              <p>{tip.body}</p>
            </div>
          </div>

          <div className="hm-veh-next-card">
            <div className="hm-veh-section-label">✅ Next best steps</div>
            <div className="hm-veh-next-list">
              <span>Scan insurance or registration cards</span>
              <span>Add the latest service receipt</span>
              <span>Check recalls before road trips</span>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function VehiclesPage() {
  const [vehicles, setVehicles]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState("");
  const [selectedVehicle, setSelected] = useState(null);
  const [detailTab, setDetailTab]     = useState("overview");
  const [showAdd, setShowAdd]         = useState(false);
  const [showHeaderPhotoModal, setShowHeaderPhotoModal] = useState(false);
  const [headerPhotoSaving, setHeaderPhotoSaving] = useState(false);
  const headerPhotoInputRef = useRef(null);
  const presentation = useFormPresentation();
  const showHeaderPhotoNotice = useMinLoad(headerPhotoSaving, 700);

  const loadVehicles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await vehiclesApi.list();
      setVehicles(data || []);
    } catch (err) {
      setError(err.message || "Could not load vehicles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadVehicles(); }, [loadVehicles]);

  function handleSaved(created) {
    setVehicles(prev => [created, ...prev]);
    setShowAdd(false);
    setDetailTab("overview");
    setSelected(created);
  }

  function selectVehicle(vehicle) {
    setDetailTab("overview");
    setSelected(vehicle);
  }

  function handleUpdate(updated) {
    setVehicles(prev => prev.map(v => v.id === updated.id ? updated : v));
    if (selectedVehicle?.id === updated.id) setSelected(updated);
  }

  async function handleHeaderPhotoFile(file) {
    if (!file) return;
    const targetVehicle = vehicles.find(v => isVehiclePhoto(v.photo_url)) || vehicles[0];
    if (!targetVehicle) {
      setError("Add a vehicle first, then upload its photo.");
      return;
    }
    setHeaderPhotoSaving(true);
    setError("");
    try {
      const photo = await compressVehicleImage(file);
      const updated = await vehiclesApi.update(targetVehicle.id, { photo_url: photo });
      handleUpdate(updated);
    } catch (err) {
      setError(err.message || "Could not save vehicle photo");
    } finally {
      setHeaderPhotoSaving(false);
    }
  }

  async function handleDelete(id) {
    try {
      await vehiclesApi.remove(id);
      setVehicles(prev => prev.filter(v => v.id !== id));
      setSelected(null);
      setDetailTab("overview");
    } catch (err) {
      setError(err.message || "Could not remove vehicle");
      throw err;
    }
  }

  const totalRecalls = vehicles.reduce((n, v) => n + (v.recall_count || 0), 0);
  const uploadedPhotoVehicles = vehicles.filter(v => isVehiclePhoto(v.photo_url));
  const headerPhotoVehicles = uploadedPhotoVehicles.slice(0, 1);
  const vehicleHeaderImages = headerPhotoVehicles.map(v => v.photo_url);
  const pageShellStyle = { width: "100%", margin: "0 auto" };

  // Detail view
  if (selectedVehicle) {
    const detail = (
      <VehicleDetail
        vehicle={selectedVehicle}
        activeTab={detailTab}
        onTabChange={setDetailTab}
        onBack={() => { setSelected(null); setDetailTab("overview"); }}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
      />
    );
    if (presentation === "modal") {
      return (
        <div className="hm-veh-page" style={pageShellStyle}>
          <CenteredModal title={selectedVehicle.nickname || selectedVehicle.make || "Vehicle"} onClose={() => { setSelected(null); setDetailTab("overview"); }} width={760}>
            {detail}
          </CenteredModal>
        </div>
      );
    }
    return (
      <div className="hm-veh-page" style={pageShellStyle}>
        {detail}
      </div>
    );
  }

  return (
    <div className="hm-veh-page" style={pageShellStyle}>
      {showHeaderPhotoNotice && <SavingNotice title="Saving" message="Updating vehicle photo..." />}
      <ModuleHeader
        title="Your garage"
        subtitle="Track service history, mileage, recalls, and maintenance for every vehicle your household owns."
        image={vehicleHeaderImages[0] || IMG_VEHICLES}
        galleryImages={vehicleHeaderImages}
        className={vehicleHeaderImages.length ? "" : "hm-module-header--vehicle-default"}
      >
        {vehicles.length > 0 && (
          <div className="hm-module-stat">
            {vehicles.length} <span>{vehicles.length === 1 ? "vehicle" : "vehicles"}</span>
          </div>
        )}
        {totalRecalls > 0 && (
          <div className="hm-module-stat hm-module-stat--alert">
            {totalRecalls}<span>open {totalRecalls === 1 ? "recall" : "recalls"}</span>
          </div>
        )}
      </ModuleHeader>

      {error && <div className="hm-inline-error" style={{ marginBottom: 12 }}>{error}</div>}

      {vehicles.length > 0 && (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          <button type="button" className="hm-vault-manual-btn hm-veh-edit-cover-btn" onClick={() => setShowHeaderPhotoModal(true)}>
            <span aria-hidden="true">▧</span>
            Edit cover
          </button>
          <button type="button" className="hm-veh-add-btn" onClick={() => setShowAdd(true)}>
            + Add vehicle
          </button>
        </div>
      )}
      <GarageView
        vehicles={vehicles}
        loading={loading}
        onSelect={selectVehicle}
        onAdd={() => setShowAdd(true)}
      />

      {showAdd && <AddVehicleForm onSaved={handleSaved} onCancel={() => setShowAdd(false)} />}

      {showHeaderPhotoModal && (
        <Modal title="Edit cover" onClose={() => setShowHeaderPhotoModal(false)}>
          {vehicles.length === 0 && (
            <div className="hm-vault-empty" style={{ marginBottom: 16 }}>
              Add a vehicle first.
            </div>
          )}

          {vehicles.length > 0 && (
            <div className="hm-veh-header-simple-upload">
              {uploadedPhotoVehicles[0]?.photo_url && (
                <div className="hm-veh-header-photo-thumb">
                  <img src={uploadedPhotoVehicles[0].photo_url} alt="" />
                </div>
              )}
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => headerPhotoInputRef.current?.click()}
                disabled={headerPhotoSaving}
              >
                {headerPhotoSaving ? "Uploading..." : uploadedPhotoVehicles.length ? "Replace vehicle photo" : "Upload vehicle photo"}
              </button>
              <input
                ref={headerPhotoInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                disabled={headerPhotoSaving}
                onChange={e => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  handleHeaderPhotoFile(file);
                }}
              />
            </div>
          )}

          <div className="flex-between" style={{ justifyContent: "space-between", gap: 10 }}>
            <span className="text-sm text-muted">
              {uploadedPhotoVehicles.length
                ? "Using uploaded vehicle photo"
                : "No vehicle photo uploaded yet"}
            </span>
            <button type="button" className="btn btn-primary" onClick={() => setShowHeaderPhotoModal(false)}>
              Done
            </button>
          </div>
        </Modal>
      )}
      {!showAdd && <VehiclesStickyAsk />}
    </div>
  );
}
